/**
 * Collaboration Details Page
 * Comprehensive view of a single collaboration with lead info,
 * team members, commission structure, activity timeline, and actions
 *
 * Module 5C: Full lead details view with documents and notes management
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Timeline } from 'primereact/timeline';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import { Chip } from 'primereact/chip';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
    collaborationService,
    getStatusConfig,
    getRoleConfig,
    getCommissionModelConfig,
    getContributionLevelConfig,
    getMemberStatusConfig,
    formatCurrency,
    formatLargeAmount,
    formatTimeAgo,
    getMilestoneStatus,
    getActivityIcon,
    formatActivityText,
    canPerformAction,
    COLLABORATION_STATUS,
    MEMBER_ROLES,
    COMMISSION_MODELS,
    DISPUTE_REASONS,
    DISPUTE_RESOLUTIONS,
    CANCELLATION_REASONS
} from '../../services/collaborationService';

import { getLoanProductConfig, formatLoanAmount, marketplaceService } from '../../services/marketplaceService';
import { getProcessingStatusConfig } from '../../services/leadProcessingService';
import { bankOptions } from '../../utils/bankOptions';

// Dynamic document components
import useMarketplaceDocuments from '../../hooks/useMarketplaceDocuments';
import MarketplaceDocumentsSection from '../../components/marketplace/MarketplaceDocumentsSection';

// Module 7: Conversion tab
import { ConversionTab } from '../../components/collaboration';

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
        help: { background: 'transparent', border: '1.5px solid #A855F7', color: '#A855F7' },
    };
    return colors[severity] || colors.secondary;
};

const CollaborationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [collaboration, setCollaboration] = useState(null);
    const [members, setMembers] = useState([]);
    const [activities, setActivities] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Module 5C: Documents state
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    // Document viewer modal state
    const [documentModalVisible, setDocumentModalVisible] = useState(false);
    const [selectedDocumentForModal, setSelectedDocumentForModal] = useState(null);

    // Dynamic document system — resolves document definitions from lead_data master values
    const leadDataForDocs = collaboration?.lead?.lead_data || null;
    const {
        resolvedDocs, groupedDocs, loadingDocMapping,
        uploadedDocs, uploadingDocs, uploadProgress,
        getUploadedFilesForDefinition, handleDocumentSelect, handleDocumentRemove,
    } = useMarketplaceDocuments({
        leadData: leadDataForDocs,
        collaborationId: id,
        documents,
        canUpload: collaboration?.status === 'active',
        toast,
    });

    // Module 5C: Notes state
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [showNoteDialog, setShowNoteDialog] = useState(false);
    const [noteData, setNoteData] = useState({ content: '', is_pinned: false });

    // Negotiation history state
    const [negotiations, setNegotiations] = useState([]);
    const [negotiationsLoading, setNegotiationsLoading] = useState(false);
    const [interestDetails, setInterestDetails] = useState(null);

    // All lead interests (for owner view - shows all negotiations on this lead)
    const [allLeadInterests, setAllLeadInterests] = useState([]);
    const [expandedInterests, setExpandedInterests] = useState({});  // Track which interests are expanded

    // Module 5C: Lead editing state
    const [showEditLeadDialog, setShowEditLeadDialog] = useState(false);
    const [editLeadData, setEditLeadData] = useState({});
    const [editLeadProcessing, setEditLeadProcessing] = useState(false);

    // Commission preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState({
        loan_amount_disbursed: 0,
        total_commission: 0
    });
    const [previewResult, setPreviewResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Dialog states
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showDisputeDialog, setShowDisputeDialog] = useState(false);
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);

    // Complete dialog data
    const [completeData, setCompleteData] = useState({
        loan_amount_disbursed: 0,
        total_commission: 0,
        notes: ''
    });

    // Cancel dialog data
    const [cancelData, setCancelData] = useState({
        reason: null,
        notes: ''
    });

    // Dispute dialog data
    const [disputeData, setDisputeData] = useState({
        reason: null,
        description: '',
        requested_resolution: null,
        against_member_id: null
    });

    // Leave dialog data
    const [leaveReason, setLeaveReason] = useState('');

    // Helper to get lead data
    const getLeadData = () => {
        return collaboration?.lead_snapshot || collaboration?.lead || collaboration?.marketplace_lead?.lead_snapshot || {};
    };

    // Helper to check if user is owner
    const isOwner = () => {
        return collaboration?.my_role === 'owner';
    };

    // Helper to check if collaboration is active
    const isActive = () => {
        return collaboration?.status === 'active';
    };

    // Helper to get grade color (matching ListingDetail)
    const getGradeColor = (grade) => {
        const colors = { 'A': 'green', 'B': 'blue', 'C': 'orange', 'D': 'yellow', 'E': 'red' };
        return colors[grade] || 'gray';
    };

    // Helper to get grade description
    const getGradeDescription = (grade) => {
        const descriptions = {
            'A': 'Premium quality lead with verified documents and excellent credentials',
            'B': 'High quality lead with good credentials and documentation',
            'C': 'Standard lead with basic verification completed',
            'D': 'Lead requires additional verification steps',
            'E': 'Basic lead information available for review'
        };
        return descriptions[grade] || 'Lead quality being assessed';
    };

    // Calculate days active
    const getDaysActive = () => {
        if (!collaboration?.created_at) return 0;
        return Math.floor((Date.now() - new Date(collaboration.created_at)) / (1000 * 60 * 60 * 24));
    };

    // Fetch collaboration details
    const fetchCollaboration = useCallback(async () => {
        setLoading(true);
        try {
            const response = await collaborationService.getCollaborationDetails(id);
            if (response.success) {
                const data = response.data;
                setCollaboration(data);
                setMembers(data.members || []);
                setActivities(data.activities || []);

                // Set preview data defaults from lead
                const lead = data.lead_snapshot || data.lead || {};
                setPreviewData({
                    loan_amount_disbursed: lead.loan_amount_required || lead.loan_amount || 0,
                    total_commission: 0
                });
                setCompleteData(prev => ({
                    ...prev,
                    loan_amount_disbursed: lead.loan_amount_required || lead.loan_amount || 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch collaboration:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load collaboration details'
            });
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchCollaboration();
        }
    }, [id, fetchCollaboration]);

    // Handle URL action params
    useEffect(() => {
        const action = searchParams.get('action');
        if (action && collaboration) {
            switch (action) {
                case 'complete':
                    if (isOwner() && isActive()) setShowCompleteDialog(true);
                    break;
                case 'dispute':
                    if (isActive()) setShowDisputeDialog(true);
                    break;
                case 'invite':
                    // Will navigate to invite dialog when implemented
                    break;
                default:
                    break;
            }
        }
    }, [searchParams, collaboration]);

    // Preview commission
    const handlePreviewCommission = async () => {
        if (!previewData.loan_amount_disbursed) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please enter disbursed amount'
            });
            return;
        }

        setPreviewLoading(true);
        try {
            const response = await collaborationService.previewCommission(id, previewData);
            if (response.success) {
                setPreviewResult(response.data);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to preview commission'
            });
        } finally {
            setPreviewLoading(false);
        }
    };

    // Complete collaboration
    const handleComplete = async () => {
        if (!completeData.loan_amount_disbursed || !completeData.total_commission) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please enter disbursed amount and total commission'
            });
            return;
        }

        setProcessing(true);
        try {
            const response = await collaborationService.completeCollaboration(id, completeData);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Completed!',
                    detail: 'Lead deal completed and commissions distributed'
                });
                setShowCompleteDialog(false);
                fetchCollaboration();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to complete lead deal'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Cancel collaboration
    const handleCancel = async () => {
        if (!cancelData.reason) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please select a cancellation reason'
            });
            return;
        }

        setProcessing(true);
        try {
            const response = await collaborationService.cancelCollaboration(id, cancelData);
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Cancelled',
                    detail: 'Lead deal has been cancelled'
                });
                setShowCancelDialog(false);
                fetchCollaboration();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to cancel lead deal'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Raise dispute
    const handleDispute = async () => {
        if (!disputeData.reason || !disputeData.description || disputeData.description.length < 50) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please provide reason and detailed description (min 50 characters)'
            });
            return;
        }

        setProcessing(true);
        try {
            const response = await collaborationService.raiseDispute(id, disputeData);
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Dispute Raised',
                    detail: 'Your dispute has been submitted for review'
                });
                setShowDisputeDialog(false);
                fetchCollaboration();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to raise dispute'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Leave collaboration
    const handleLeave = async () => {
        setProcessing(true);
        try {
            const response = await collaborationService.leaveCollaboration(id, { reason: leaveReason });
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Left',
                    detail: 'You have left the lead deal'
                });
                navigate('/app/collaborations');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to leave lead deal'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Remove member
    const handleRemoveMember = (member) => {
        confirmDialog({
            message: `Are you sure you want to remove ${member.name} from this lead deal?`,
            header: 'Remove Member',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await collaborationService.removeMember(id, member.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Removed',
                            detail: `${member.name} has been removed`
                        });
                        fetchCollaboration();
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to remove member'
                    });
                }
            }
        });
    };

    // =========================================================================
    // MODULE 5C: LEAD DATA MANAGEMENT
    // =========================================================================

    // Open edit lead dialog
    const openEditLeadDialog = () => {
        const lead = getLeadData();
        setEditLeadData({
            full_name: lead.full_name || lead.borrower_name || '',
            first_name: lead.first_name || '',
            middle_name: lead.middle_name || '',
            last_name: lead.last_name || '',
            phone: lead.phone || '',
            email: lead.email || '',
            pan: lead.pan || '',
            gender: lead.gender || '',
            address: lead.address || '',
            city: lead.city || '',
            state: lead.state || '',
            state_code: lead.state_code || '',
            pincode: lead.pincode || '',
            residency: lead.residency || '',
            loan_amount_required: lead.loan_amount_required || lead.loan_amount || 0,
            loan_amount: lead.loan_amount || '',
            interest_rate: lead.interest_rate || '',
            period: lead.period || '',
            emi: lead.emi || '',
            cibil_score: lead.cibil_score || '',
            cibil_check_done: lead.cibil_check_done || false,
            net_salary: lead.net_salary || '',
            employment_type: lead.employment_type || '',
            company_name: lead.company_name || '',
            business_type: lead.business_type || '',
            annual_turnover: lead.annual_turnover || '',
            note: lead.note || '',
            referred_by: lead.referred_by || '',
            preferred_bank: Array.isArray(lead.preferred_bank) ? lead.preferred_bank : (lead.preferred_bank ? [lead.preferred_bank] : [])
        });
        setShowEditLeadDialog(true);
    };

    // Handle lead update
    const handleUpdateLead = async () => {
        setEditLeadProcessing(true);
        try {
            // Ensure proper types for all fields before sending
            const sanitizedData = {
                ...editLeadData,
                // Ensure string fields are strings (not null/undefined/number)
                full_name: editLeadData.full_name ? String(editLeadData.full_name) : null,
                first_name: editLeadData.first_name ? String(editLeadData.first_name) : null,
                middle_name: editLeadData.middle_name ? String(editLeadData.middle_name) : null,
                last_name: editLeadData.last_name ? String(editLeadData.last_name) : null,
                phone: editLeadData.phone ? String(editLeadData.phone) : null,
                email: editLeadData.email ? String(editLeadData.email) : null,
                pan: editLeadData.pan ? String(editLeadData.pan) : null,
                gender: editLeadData.gender ? String(editLeadData.gender) : null,
                address: editLeadData.address ? String(editLeadData.address) : null,
                city: editLeadData.city ? String(editLeadData.city) : null,
                state: editLeadData.state ? String(editLeadData.state) : null,
                state_code: editLeadData.state_code ? String(editLeadData.state_code) : null,
                pincode: editLeadData.pincode ? String(editLeadData.pincode) : null,
                residency: editLeadData.residency ? String(editLeadData.residency) : null,
                employment_type: editLeadData.employment_type ? String(editLeadData.employment_type) : null,
                company_name: editLeadData.company_name ? String(editLeadData.company_name) : null,
                business_type: editLeadData.business_type ? String(editLeadData.business_type) : null,
                note: editLeadData.note ? String(editLeadData.note) : null,
                referred_by: editLeadData.referred_by ? String(editLeadData.referred_by) : null,
                preferred_bank: Array.isArray(editLeadData.preferred_bank) && editLeadData.preferred_bank.length > 0 ? editLeadData.preferred_bank : null,
                // Ensure numeric fields are numbers or null
                loan_amount_required: editLeadData.loan_amount_required ? Number(editLeadData.loan_amount_required) : null,
                loan_amount: editLeadData.loan_amount ? Number(editLeadData.loan_amount) : null,
                interest_rate: editLeadData.interest_rate ? Number(editLeadData.interest_rate) : null,
                period: editLeadData.period ? Number(editLeadData.period) : null,
                emi: editLeadData.emi ? Number(editLeadData.emi) : null,
                cibil_score: editLeadData.cibil_score ? Number(editLeadData.cibil_score) : null,
                net_salary: editLeadData.net_salary ? Number(editLeadData.net_salary) : null,
                annual_turnover: editLeadData.annual_turnover ? Number(editLeadData.annual_turnover) : null,
            };
            const response = await collaborationService.updateLeadData(id, sanitizedData);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Lead information updated successfully'
                });
                setShowEditLeadDialog(false);
                fetchCollaboration();
                fetchActivities();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to update lead'
            });
        } finally {
            setEditLeadProcessing(false);
        }
    };

    // =========================================================================
    // MODULE 5C: ACTIVITIES MANAGEMENT
    // =========================================================================

    // Fetch activities
    const fetchActivities = async () => {
        try {
            const response = await collaborationService.getActivities(id, { limit: 100 });
            if (response.success) {
                setActivities(response.data?.activities || []);
            }
        } catch (error) {
            console.error('Failed to fetch activities:', error);
        }
    };

    // =========================================================================
    // MODULE 5C: DOCUMENTS MANAGEMENT
    // =========================================================================

    // Fetch documents
    const fetchDocuments = async () => {
        setDocumentsLoading(true);
        try {
            const response = await collaborationService.getDocuments(id);
            if (response.success) {
                setDocuments(response.data?.documents || []);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setDocumentsLoading(false);
        }
    };

    // Get document icon based on type
    const getDocumentIcon = (doc) => {
        if (doc?.icon_class) return doc.icon_class;
        if (doc?.is_pdf) return 'pi pi-file-pdf';
        if (doc?.is_image) return 'pi pi-image';

        const extension = doc?.file_extension?.toLowerCase();
        const iconMap = {
            'pdf': 'pi-file-pdf',
            'jpg': 'pi-image',
            'jpeg': 'pi-image',
            'png': 'pi-image',
            'gif': 'pi-image',
            'doc': 'pi-file-word',
            'docx': 'pi-file-word',
            'xls': 'pi-file-excel',
            'xlsx': 'pi-file-excel'
        };
        return `pi ${iconMap[extension] || 'pi-file'}`;
    };

    // Download document
    const handleDownloadDocument = async (doc) => {
        const fileUrl = doc?.file_url || doc?.file_path;
        if (!fileUrl) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Document URL not available',
                life: 3000
            });
            return;
        }
        window.open(fileUrl, '_blank');
    };

    // Handle document delete
    const handleDeleteDocument = (doc) => {
        confirmDialog({
            message: `Are you sure you want to delete "${doc.document_name || doc.file_name}"?`,
            header: 'Delete Document',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await collaborationService.deleteDocument(id, doc.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Deleted',
                            detail: 'Document deleted successfully'
                        });
                        fetchDocuments();
                        fetchActivities();
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to delete document'
                    });
                }
            }
        });
    };

    // =========================================================================
    // MODULE 5C: NOTES MANAGEMENT
    // =========================================================================

    // Fetch notes
    const fetchNotes = async () => {
        setNotesLoading(true);
        try {
            const response = await collaborationService.getNotes(id);
            if (response.success) {
                setNotes(response.data?.notes || []);
            }
        } catch (error) {
            console.error('Failed to fetch notes:', error);
        } finally {
            setNotesLoading(false);
        }
    };

    // Fetch negotiation history
    const fetchNegotiations = useCallback(async () => {
        const interestId = collaboration?.marketplace_interest_id || collaboration?.interest_id;
        const marketplaceLeadId = collaboration?.marketplace_lead_id || collaboration?.marketplaceLead?.id;
        const userIsOwner = isOwner();

        setNegotiationsLoading(true);
        try {
            // For owners: Fetch ALL interests on this lead (to see all negotiations)
            if (userIsOwner && marketplaceLeadId) {
                const allInterestsResponse = await marketplaceService.getLeadInterests(marketplaceLeadId);
                if (allInterestsResponse.success) {
                    const interests = allInterestsResponse.data?.interests || allInterestsResponse.data || [];
                    setAllLeadInterests(Array.isArray(interests) ? interests : []);

                    // Auto-expand the accepted interest (current collaboration)
                    if (interestId) {
                        setExpandedInterests({ [interestId]: true });
                    }
                }
            }

            // Also fetch details for the current collaboration's interest
            if (interestId) {
                const response = await marketplaceService.getInterestDetails(interestId);
                if (response.success) {
                    setInterestDetails(response.data);
                    setNegotiations(response.data?.negotiations || []);
                }
            } else {
                console.log('No interest ID available for negotiation history');
            }
        } catch (error) {
            console.error('Failed to fetch negotiation history:', error);
        } finally {
            setNegotiationsLoading(false);
        }
    }, [collaboration]);

    // Fetch details for a specific interest (when accordion is expanded)
    const fetchInterestDetails = async (interestId) => {
        try {
            const response = await marketplaceService.getInterestDetails(interestId);
            if (response.success) {
                // Update the interest in allLeadInterests with full details
                setAllLeadInterests(prev => prev.map(interest =>
                    interest.id === interestId
                        ? { ...interest, ...response.data, negotiations: response.data?.negotiations || [] }
                        : interest
                ));
            }
        } catch (error) {
            console.error('Failed to fetch interest details:', error);
        }
    };

    // Toggle interest expansion
    const toggleInterestExpansion = async (interestId) => {
        const isExpanding = !expandedInterests[interestId];
        setExpandedInterests(prev => ({ ...prev, [interestId]: isExpanding }));

        // Fetch details if expanding and not already loaded
        if (isExpanding) {
            const interest = allLeadInterests.find(i => i.id === interestId);
            if (!interest?.negotiations) {
                await fetchInterestDetails(interestId);
            }
        }
    };

    // Get status badge config for interests
    const getInterestStatusBadge = (status) => {
        const configs = {
            pending: { severity: 'warning', icon: 'pi pi-clock', label: 'Pending' },
            countered: { severity: 'info', icon: 'pi pi-sync', label: 'Counter Offer' },
            accepted: { severity: 'success', icon: 'pi pi-check-circle', label: 'Accepted' },
            rejected: { severity: 'danger', icon: 'pi pi-times-circle', label: 'Rejected' },
            withdrawn: { severity: 'secondary', icon: 'pi pi-undo', label: 'Withdrawn' },
            expired: { severity: 'secondary', icon: 'pi pi-calendar-times', label: 'Expired' }
        };
        return configs[status] || { severity: 'secondary', icon: 'pi pi-question', label: status };
    };

    // Handle add note
    const handleAddNote = async () => {
        if (!noteData.content.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please enter note content'
            });
            return;
        }

        setProcessing(true);
        try {
            const response = await collaborationService.addNote(id, noteData);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Added',
                    detail: 'Note added successfully'
                });
                setShowNoteDialog(false);
                setNoteData({ content: '', is_pinned: false });
                fetchNotes();
                fetchActivities();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to add note'
            });
        } finally {
            setProcessing(false);
        }
    };

    // Handle delete note
    const handleDeleteNote = (note) => {
        confirmDialog({
            message: 'Are you sure you want to delete this note?',
            header: 'Delete Note',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await collaborationService.deleteNote(id, note.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Deleted',
                            detail: 'Note deleted successfully'
                        });
                        fetchNotes();
                        fetchActivities();
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to delete note'
                    });
                }
            }
        });
    };

    // Handle toggle note pin
    const handleToggleNotePin = async (note) => {
        try {
            const response = await collaborationService.toggleNotePin(id, note.id);
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: note.is_pinned ? 'Unpinned' : 'Pinned',
                    detail: `Note ${note.is_pinned ? 'unpinned' : 'pinned'} successfully`
                });
                fetchNotes();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to toggle pin'
            });
        }
    };

    // Load documents, notes, and negotiations when tab changes (Documents=0, Notes=1, Negotiations=2)
    useEffect(() => {
        if (collaboration && activeTabIndex === 0) {
            fetchDocuments();
        } else if (collaboration && activeTabIndex === 1) {
            fetchNotes();
        } else if (collaboration && activeTabIndex === 2) {
            fetchNegotiations();
        }
    }, [activeTabIndex, collaboration, fetchNegotiations]);

    // Render Quick Stats Section - Compact
    const renderQuickStats = () => {
        const lead = getLeadData();
        return (
            <div className="grid mb-2">
                <div className="col-6 md:col-3 p-1">
                    <div className="flex align-items-center gap-2 p-2 surface-100 border-round h-full">
                        <div className="flex align-items-center justify-content-center border-round flex-shrink-0" style={{ width: '28px', height: '28px', backgroundColor: 'var(--primary-100)' }}>
                            <i className="pi pi-info-circle text-primary" style={{ fontSize: '0.75rem' }} />
                        </div>
                        <div>
                            <div className="text-900 font-bold" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
                                {getProcessingStatusConfig(lead.lead_status || collaboration?.status).label}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead Status</div>
                        </div>
                    </div>
                </div>
                <div className="col-6 md:col-3 p-1">
                    <div className="flex align-items-center gap-2 p-2 surface-100 border-round h-full">
                        <div className="flex align-items-center justify-content-center border-round flex-shrink-0" style={{ width: '28px', height: '28px', backgroundColor: 'var(--green-100)' }}>
                            <i className="pi pi-users text-green-600" style={{ fontSize: '0.75rem' }} />
                        </div>
                        <div>
                            <div className="text-900 font-bold" style={{ fontSize: '0.9rem' }}>
                                {members.length}/{collaboration?.max_collaborators || 5}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Team</div>
                        </div>
                    </div>
                </div>
                <div className="col-6 md:col-3 p-1">
                    <div className="flex align-items-center gap-2 p-2 surface-100 border-round h-full">
                        <div className="flex align-items-center justify-content-center border-round flex-shrink-0" style={{ width: '28px', height: '28px', backgroundColor: 'var(--pink-100)' }}>
                            <i className="pi pi-percentage text-pink-600" style={{ fontSize: '0.75rem' }} />
                        </div>
                        <div>
                            <div className="text-green-600 font-bold" style={{ fontSize: '0.9rem' }}>
                                {collaboration?.my_commission_percentage || 0}%
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Your Share</div>
                        </div>
                    </div>
                </div>
                <div className="col-6 md:col-3 p-1">
                    <div className="flex align-items-center gap-2 p-2 surface-100 border-round h-full">
                        <div className="flex align-items-center justify-content-center border-round flex-shrink-0" style={{ width: '28px', height: '28px', backgroundColor: 'var(--orange-100)' }}>
                            <i className="pi pi-clock text-orange-600" style={{ fontSize: '0.75rem' }} />
                        </div>
                        <div>
                            <div className="text-900 font-bold" style={{ fontSize: '0.9rem' }}>
                                {getDaysActive()}d
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Active</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Borrower Information Section - Compact
    const renderBorrowerInfo = () => {
        const lead = getLeadData();
        const hasFullAccess = collaboration?.has_full_access || isOwner();

        return (
            <div className="mb-2">
                <div className="flex align-items-center gap-1 mb-1">
                    <i className="pi pi-user text-primary" style={{ fontSize: '0.75rem' }} />
                    <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Borrower Information</h4>
                    <Tag
                        value={hasFullAccess ? "Full Access" : "Protected"}
                        icon={hasFullAccess ? "pi pi-lock-open" : "pi pi-lock"}
                        className="ml-auto"
                        style={{ ...outlineTagStyle(hasFullAccess ? 'success' : 'secondary'), fontSize: '0.55rem', padding: '1px 4px' }}
                    />
                </div>
                <div className="surface-50 border-round p-2">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="flex align-items-center gap-2 mb-2">
                                <div className="flex align-items-center justify-content-center bg-primary-100 border-circle flex-shrink-0"
                                     style={{ width: '24px', height: '24px' }}>
                                    <i className="pi pi-user text-primary" style={{ fontSize: '0.65rem' }} />
                                </div>
                                <div className="overflow-hidden">
                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>Full Name</div>
                                    <div className="text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.75rem' }}>
                                        {lead.full_name || lead.borrower_name || 'N/A'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex align-items-center gap-2 mb-2">
                                <div className="flex align-items-center justify-content-center bg-green-100 border-circle flex-shrink-0"
                                     style={{ width: '24px', height: '24px' }}>
                                    <i className="pi pi-phone text-green-600" style={{ fontSize: '0.65rem' }} />
                                </div>
                                <div className="overflow-hidden">
                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>Phone</div>
                                    <div className="text-900 font-medium" style={{ fontSize: '0.75rem' }}>{lead.phone || 'N/A'}</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="flex align-items-center gap-2 mb-2">
                                <div className="flex align-items-center justify-content-center bg-blue-100 border-circle flex-shrink-0"
                                     style={{ width: '24px', height: '24px' }}>
                                    <i className="pi pi-envelope text-blue-600" style={{ fontSize: '0.65rem' }} />
                                </div>
                                <div className="overflow-hidden">
                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>Email</div>
                                    <div className="text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.75rem' }}>{lead.email || 'N/A'}</div>
                                </div>
                            </div>
                            <div className="flex align-items-center gap-2 mb-2">
                                <div className="flex align-items-center justify-content-center bg-purple-100 border-circle flex-shrink-0"
                                     style={{ width: '24px', height: '24px' }}>
                                    <i className="pi pi-id-card text-purple-600" style={{ fontSize: '0.65rem' }} />
                                </div>
                                <div className="overflow-hidden">
                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>PAN</div>
                                    <div className="text-900 font-medium" style={{ fontSize: '0.75rem' }}>{lead.pan || 'N/A'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Lead Details Grid - Compact
    const renderLeadDetailsGrid = () => {
        const lead = getLeadData();
        const productConfig = getLoanProductConfig(lead.product_type_detailed || lead.product_type || 'personal_loan');

        const detailItems = [
            {
                label: 'Loan Amount',
                value: formatLargeAmount(lead.loan_amount_required || lead.loan_amount || 0),
                icon: 'pi-indian-rupee',
                color: 'green'
            },
            {
                label: 'Employment',
                value: (lead.employment_type || 'Not Specified')?.replace(/_/g, ' '),
                icon: 'pi-briefcase',
                color: 'blue',
                capitalize: true
            },
            {
                label: 'Location',
                value: `${lead.city || 'N/A'}${lead.state ? `, ${lead.state}` : ''}`,
                icon: 'pi-map-marker',
                color: 'orange'
            },
            {
                label: 'CIBIL Score',
                value: lead.cibil_score || 'Not Available',
                icon: 'pi-chart-line',
                color: 'purple'
            },
            {
                label: 'Product Type',
                value: productConfig.label,
                icon: productConfig.icon,
                color: 'primary'
            },
            {
                label: 'Created',
                value: formatTimeAgo(collaboration?.created_at),
                icon: 'pi-calendar',
                color: 'cyan'
            }
        ];

        return (
            <div className="mb-2">
                <div className="flex align-items-center gap-1 mb-1">
                    <i className="pi pi-file text-primary" style={{ fontSize: '0.75rem' }} />
                    <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Lead Details</h4>
                </div>
                <div className="grid">
                    {detailItems.map((item, idx) => (
                        <div key={idx} className="col-6 md:col-4 p-1">
                            <div className="surface-50 border-round p-2 h-full">
                                <div className="flex align-items-center gap-1 mb-1">
                                    <i className={`pi ${item.icon} text-${item.color}-500`} style={{ fontSize: '0.6rem' }} />
                                    <span className="text-500" style={{ fontSize: '0.6rem' }}>{item.label}</span>
                                </div>
                                <div className={`text-900 font-semibold ${item.capitalize ? 'capitalize' : ''} white-space-nowrap overflow-hidden text-overflow-ellipsis`} style={{ fontSize: '0.75rem' }}>
                                    {item.value}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render Lead Quality Assessment Card - Compact
    const renderQualityAssessment = () => {
        const lead = getLeadData();
        const grade = lead.quality_grade || 'C';
        const score = lead.quality_score || 50;

        return (
            <Card className="shadow-1 mb-2 p-2">
                <div className="flex align-items-center gap-1 mb-2">
                    <i className="pi pi-star text-primary" style={{ fontSize: '0.75rem' }} />
                    <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Lead Quality Assessment</h4>
                </div>

                <div className="flex flex-column md:flex-row gap-2 mb-2">
                    <div className="flex align-items-center gap-2">
                        <div
                            className={`flex align-items-center justify-content-center bg-${getGradeColor(grade)}-100 border-round flex-shrink-0`}
                            style={{ width: '40px', height: '40px' }}
                        >
                            <span className={`text-${getGradeColor(grade)}-600 font-bold`} style={{ fontSize: '1.25rem' }}>
                                {grade}
                            </span>
                        </div>
                        <div>
                            <div className="text-900 font-bold" style={{ fontSize: '1rem' }}>{score}/100</div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Quality Score</div>
                        </div>
                    </div>
                    <Divider layout="vertical" className="hidden md:block my-0" />
                    <div className="flex-1">
                        <p className="text-600 m-0 line-height-3" style={{ fontSize: '0.7rem' }}>
                            {getGradeDescription(grade)}
                        </p>
                    </div>
                </div>

                <ProgressBar
                    value={score}
                    showValue={false}
                    className="mb-2"
                    style={{ height: '4px' }}
                 aria-label="Progress" />

                {/* Quality Breakdown */}
                <div className="grid">
                    {[
                        {
                            label: 'CIBIL Score',
                            value: lead.cibil_score ? `${lead.cibil_score}` : 'N/A',
                            check: !!lead.cibil_score
                        },
                        {
                            label: 'Bank Statement',
                            value: lead.bank_statement_analyzed ? 'Analyzed' : 'Pending',
                            check: lead.bank_statement_analyzed
                        },
                        {
                            label: 'Documents',
                            value: documents.length > 0 ? `${documents.length} Uploaded` : 'None',
                            check: documents.length > 0
                        }
                    ].map((item, idx) => (
                        <div key={idx} className="col-12 md:col-4 p-1">
                            <div className="flex align-items-center gap-2 p-2 surface-50 border-round">
                                <div
                                    className={`flex align-items-center justify-content-center border-circle flex-shrink-0 ${item.check ? 'bg-green-100' : 'bg-gray-100'}`}
                                    style={{ width: '20px', height: '20px' }}
                                >
                                    <i className={`pi ${item.check ? 'pi-check text-green-600' : 'pi-times text-gray-400'}`} style={{ fontSize: '0.6rem' }} />
                                </div>
                                <div>
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>{item.label}</div>
                                    <div className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{item.value}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    };

    // Render Lead Status Section
    const renderLeadStatus = () => {
        const lead = getLeadData();
        if (!lead.lead_status && !lead.lead_final_status && !lead.cibil_checked_at && !lead.bank_statement_analyzed_at && !lead.loan_offer_generated_at) {
            return null;
        }

        const getStatusConfig = (status) => getProcessingStatusConfig(status);

        return (
            <Card className="shadow-1 mb-3">
                <div className="flex align-items-center gap-2 mb-4">
                    <i className="pi pi-flag text-primary" />
                    <h4 className="text-900 font-semibold m-0">Lead Status</h4>
                </div>

                <div className="grid">
                    {lead.lead_status && (
                        <div className="col-12 md:col-6">
                            <div className="surface-50 border-round p-3 mb-3">
                                <div className="text-500 text-sm mb-2">Current Status</div>
                                {(() => { const c = getStatusConfig(lead.lead_status); return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} />; })()}
                            </div>
                        </div>
                    )}
                    {lead.lead_final_status && (
                        <div className="col-12 md:col-6">
                            <div className="surface-50 border-round p-3 mb-3">
                                <div className="text-500 text-sm mb-2">Final Status</div>
                                {(() => { const c = getStatusConfig(lead.lead_final_status); return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} />; })()}
                            </div>
                        </div>
                    )}
                </div>

                {/* Verification Timeline */}
                {(lead.cibil_checked_at || lead.bank_statement_analyzed_at || lead.loan_offer_generated_at) && (
                    <div className="border-top-1 border-200 pt-3 mt-3">
                        <div className="text-500 text-sm mb-3">Verification Timeline</div>
                        <div className="flex flex-column gap-2">
                            {lead.cibil_checked_at && (
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-green-500" />
                                    <span className="text-900 text-sm">CIBIL Checked</span>
                                    <span className="text-500 text-xs ml-auto">
                                        {formatTimeAgo(lead.cibil_checked_at)}
                                    </span>
                                </div>
                            )}
                            {lead.bank_statement_analyzed_at && (
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-green-500" />
                                    <span className="text-900 text-sm">Bank Statement Analyzed</span>
                                    <span className="text-500 text-xs ml-auto">
                                        {formatTimeAgo(lead.bank_statement_analyzed_at)}
                                    </span>
                                </div>
                            )}
                            {lead.loan_offer_generated_at && (
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-green-500" />
                                    <span className="text-900 text-sm">Loan Offer Generated</span>
                                    <span className="text-500 text-xs ml-auto">
                                        {formatTimeAgo(lead.loan_offer_generated_at)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Card>
        );
    };


    // Render Employment & Financial Details
    const renderEmploymentDetails = () => {
        const lead = getLeadData();
        const hasAnyData = lead.company_name || lead.net_salary || lead.business_type ||
                          lead.annual_turnover || lead.profession_type || lead.gross_receipts_per_annum ||
                          lead.gender || lead.dob || lead.referred_by || lead.preferred_bank;

        if (!hasAnyData) return null;

        return (
            <Card className="shadow-1 mb-3">
                <div className="flex align-items-center gap-2 mb-4">
                    <i className="pi pi-briefcase text-primary" />
                    <h4 className="text-900 font-semibold m-0">Employment & Financial Details</h4>
                </div>

                <div className="grid">
                    {/* Salaried fields */}
                    {lead.employment_type === 'salaried' && (
                        <>
                            {lead.company_name && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Company</div>
                                    <div className="text-900 font-medium">{lead.company_name}</div>
                                </div>
                            )}
                            {lead.net_salary && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Net Salary</div>
                                    <div className="text-900 font-medium">{formatCurrency(lead.net_salary)}</div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Self-Employed fields */}
                    {lead.employment_type === 'self_employed' && (
                        <>
                            {lead.business_type && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Business Type</div>
                                    <div className="text-900 font-medium capitalize">{lead.business_type}</div>
                                </div>
                            )}
                            {lead.annual_turnover && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Annual Turnover</div>
                                    <div className="text-900 font-medium">{formatCurrency(lead.annual_turnover)}</div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Professional fields */}
                    {lead.employment_type === 'professional' && (
                        <>
                            {lead.profession_type && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Profession</div>
                                    <div className="text-900 font-medium capitalize">{lead.profession_type}</div>
                                </div>
                            )}
                            {lead.gross_receipts_per_annum && (
                                <div className="col-12 md:col-6 lg:col-4 mb-3">
                                    <div className="text-500 text-sm mb-1">Gross Receipts (Annual)</div>
                                    <div className="text-900 font-medium">{formatCurrency(lead.gross_receipts_per_annum)}</div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Common fields */}
                    {lead.gender && (
                        <div className="col-6 md:col-4 lg:col-3 mb-3">
                            <div className="text-500 text-sm mb-1">Gender</div>
                            <div className="text-900 font-medium capitalize">{lead.gender}</div>
                        </div>
                    )}
                    {lead.dob && (
                        <div className="col-6 md:col-4 lg:col-3 mb-3">
                            <div className="text-500 text-sm mb-1">Date of Birth</div>
                            <div className="text-900 font-medium">{lead.dob}</div>
                        </div>
                    )}
                    {lead.referred_by && (
                        <div className="col-12 md:col-6 lg:col-4 mb-3">
                            <div className="text-500 text-sm mb-1">Referred By</div>
                            <div className="text-900 font-medium">{lead.referred_by}</div>
                        </div>
                    )}
                    {lead.preferred_bank && (
                        <div className="col-12 mb-3">
                            <div className="text-500 text-sm mb-2">Preferred Banks</div>
                            <div className="flex flex-wrap gap-2">
                                {(Array.isArray(lead.preferred_bank) ? lead.preferred_bank : [lead.preferred_bank]).map((bank, idx) => (
                                    <Chip key={idx} label={bank} className="text-xs" />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    // Render Sidebar Team Members - Compact
    const renderSidebarTeamMembers = () => {
        return (
            <Card className="shadow-1 mb-2 sidebar-team-card p-2">
                <div className="flex align-items-center justify-content-between mb-2">
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-users text-primary" style={{ fontSize: '0.75rem' }} />
                        <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Team Members</h4>
                    </div>
                    <Badge value={members.length} severity="info" style={{ fontSize: '0.6rem', padding: '0 4px', minWidth: '16px', height: '14px', lineHeight: '14px' }} />
                </div>

                <div className="flex flex-column gap-2">
                    {members.map((member, idx) => (
                        <div key={idx} className="flex align-items-center gap-2">
                            <Avatar
                                label={member.name?.charAt(0) || 'U'}
                                shape="circle"
                                className="flex-shrink-0"
                                style={{
                                    backgroundColor: getRoleConfig(member.role).bgColor,
                                    color: getRoleConfig(member.role).color,
                                    width: '24px',
                                    height: '24px',
                                    fontSize: '0.65rem'
                                }}
                            />
                            <div className="flex-1 overflow-hidden">
                                <div className="flex align-items-center gap-1">
                                    <span className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.75rem' }}>{member.name}</span>
                                    {member.profile_id === collaboration?.my_profile_id && (
                                        <Tag value="You" className="flex-shrink-0" style={{ fontSize: '0.5rem', padding: '1px 3px' }} />
                                    )}
                                </div>
                                <div className="text-500" style={{ fontSize: '0.6rem' }}>
                                    {getRoleConfig(member.role).label} • {member.commission_percentage}%
                                </div>
                            </div>
                            {isOwner() && member.role !== 'owner' && isActive() && (
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-danger flex-shrink-0"
                                    style={{ padding: '0.15rem', fontSize: '0.6rem' }}
                                    onClick={() => handleRemoveMember(member)}
                                    tooltip="Remove"
                                    tooltipOptions={{ position: 'left' }}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {isOwner() && isActive() && (
                    <Button
                        label="Invite"
                        icon="pi pi-user-plus"
                        className="w-full mt-2 p-button-outlined"
                        style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                        onClick={() => navigate(`/app/collaborations/${id}/invite`)}
                    />
                )}
            </Card>
        );
    };

    // Render Sidebar Action Card (sticky) - Compact
    const renderSidebarActions = () => {
        return (
            <Card className="shadow-1 sticky p-2" style={{ top: '0.5rem' }}>
                {isOwner() ? (
                    <div className="flex flex-column gap-1">
                        {isActive() && (
                            <>
                                <Button
                                    label="Complete"
                                    icon="pi pi-check-circle"
                                    className="w-full p-button-success"
                                    style={{ fontSize: '0.75rem', padding: '0.4rem 0.5rem' }}
                                    onClick={() => setShowCompleteDialog(true)}
                                />
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times-circle"
                                    className="w-full p-button-danger p-button-outlined"
                                    style={{ fontSize: '0.75rem', padding: '0.4rem 0.5rem' }}
                                    onClick={() => setShowCancelDialog(true)}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-column gap-1">
                        {isActive() && (
                            <Button
                                label="Leave"
                                icon="pi pi-sign-out"
                                className="w-full p-button-warning p-button-outlined"
                                style={{ fontSize: '0.75rem', padding: '0.4rem 0.5rem' }}
                                onClick={() => setShowLeaveDialog(true)}
                            />
                        )}
                    </div>
                )}

                <Divider className="my-2" />

                <div className="flex flex-column gap-1">
                    {collaboration?.marketplace_lead_id && (
                        <Button
                            label="Marketplace"
                            icon="pi pi-external-link"
                            className="w-full p-button-outlined"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem' }}
                            onClick={() => navigate(`/app/marketplace/listing/${collaboration.marketplace_lead_id}`)}
                        />
                    )}
                    {isActive() && (
                        <Button
                            label="Dispute"
                            icon="pi pi-exclamation-triangle"
                            className="w-full p-button-text p-button-secondary"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.5rem' }}
                            onClick={() => setShowDisputeDialog(true)}
                        />
                    )}
                </div>
            </Card>
        );
    };

    // Render lead information card (main orchestrator) - Compact
    const renderLeadCard = () => {
        const lead = getLeadData();
        const productConfig = getLoanProductConfig(lead.product_type_detailed || lead.product_type || 'personal_loan');

        return (
            <Card className="shadow-1 mb-2 p-2">
                {/* Header with product type and grade - Compact */}
                <div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-2 mb-2 pb-2 border-bottom-1 border-200">
                    <div className="flex-1">
                        <div className="flex align-items-center gap-1 mb-1">
                            <Tag
                                value={productConfig.label}
                                icon={`pi ${productConfig.icon}`}
                                style={{ ...outlineTagStyle('info'), fontSize: '0.65rem', padding: '2px 6px' }}
                            />
                            <Tag
                                value={getStatusConfig(collaboration?.status).label}
                                style={{ ...outlineTagStyle(getStatusConfig(collaboration?.status).severity), fontSize: '0.65rem', padding: '2px 6px' }}
                            />
                        </div>
                        <h2 className="text-900 font-bold m-0 mb-1" style={{ fontSize: '1.1rem' }}>
                            {lead.borrower_name || lead.full_name || 'Lead Details'}
                        </h2>
                        <div className="text-green-600 font-bold" style={{ fontSize: '1.5rem' }}>
                            {formatLargeAmount(lead.loan_amount_required || lead.loan_amount || 0)}
                        </div>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <div
                            className={`flex align-items-center justify-content-center bg-${getGradeColor(lead.quality_grade || 'C')}-100 border-round`}
                            style={{ width: '42px', height: '42px' }}
                        >
                            <span className={`text-${getGradeColor(lead.quality_grade || 'C')}-600 font-bold`} style={{ fontSize: '1.25rem' }}>
                                {lead.quality_grade || 'C'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Quick Stats */}
                {renderQuickStats()}

                {/* Borrower Information */}
                {renderBorrowerInfo()}

                {/* Lead Details Grid */}
                {renderLeadDetailsGrid()}
            </Card>
        );
    };

    // Render commission structure card - Compact
    const renderCommissionCard = () => {
        const model = collaboration?.commission_model || 'percentage_split';
        const modelConfig = getCommissionModelConfig(model);

        return (
            <Card className="mb-2 sidebar-commission-card p-2">
                <div className="flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
                    <h3 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>
                        <i className="pi pi-chart-pie text-primary mr-1" style={{ fontSize: '0.75rem' }} />
                        Commission
                    </h3>
                    <Tag
                        value={modelConfig.label}
                        icon={`pi ${modelConfig.icon}`}
                        style={{ ...outlineTagStyle('info'), fontSize: '0.55rem', padding: '1px 4px' }}
                    />
                </div>

                {/* Your Share Highlight */}
                <div className="surface-100 border-round p-2 mb-2 text-center">
                    <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Your Commission Share</div>
                    <div className="text-green-600 font-bold mb-1" style={{ fontSize: '1.5rem' }}>
                        {model === 'percentage_split'
                            ? `${collaboration?.my_commission_percentage || 0}%`
                            : formatCurrency(collaboration?.my_commission_amount || collaboration?.my_estimated_commission || 0)
                        }
                    </div>
                    {model === 'percentage_split' && collaboration?.my_estimated_commission > 0 && (
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>
                            Est: {formatCurrency(collaboration.my_estimated_commission)}
                        </div>
                    )}
                </div>

                {/* Distribution Breakdown */}
                {model === 'percentage_split' && (
                    <div>
                        <div className="text-900 font-medium mb-2" style={{ fontSize: '0.75rem' }}>Distribution</div>
                        {members.map((member, idx) => (
                            <div key={idx} className="flex align-items-center justify-content-between mb-2">
                                <div className="flex align-items-center gap-1 overflow-hidden">
                                    <Avatar
                                        label={member.name?.charAt(0) || 'U'}
                                        shape="circle"
                                        className="flex-shrink-0"
                                        style={{
                                            backgroundColor: getRoleConfig(member.role).bgColor,
                                            color: getRoleConfig(member.role).color,
                                            width: '20px',
                                            height: '20px',
                                            fontSize: '0.6rem'
                                        }}
                                    />
                                    <span className="font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.7rem' }}>{member.name}</span>
                                    {member.profile_id === collaboration?.my_profile_id && (
                                        <Tag value="You" className="flex-shrink-0" style={{ ...outlineTagStyle('info'), fontSize: '0.5rem', padding: '1px 3px' }} />
                                    )}
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className="font-bold text-green-600" style={{ fontSize: '0.75rem' }}>{member.commission_percentage || 0}%</div>
                                </div>
                            </div>
                        ))}
                        {/* Progress bar showing total allocation */}
                        <div className="mt-2">
                            <div className="flex justify-content-between mb-1">
                                <span className="text-500" style={{ fontSize: '0.6rem' }}>Allocated</span>
                                <span className="font-medium" style={{ fontSize: '0.6rem' }}>
                                    {members.reduce((sum, m) => sum + (m.commission_percentage || 0), 0)}%
                                </span>
                            </div>
                            <ProgressBar
                                value={members.reduce((sum, m) => sum + (m.commission_percentage || 0), 0)}
                                showValue={false}
                                style={{ height: '4px' }}
                             aria-label="Progress" />
                        </div>
                    </div>
                )}

                {model === 'fixed_amount' && (
                    <DataTable value={members} className="p-datatable-sm">
                        <Column field="name" header="Member" />
                        <Column field="role" header="Role" body={(row) => (
                            <Tag value={getRoleConfig(row.role).label} style={{
                                backgroundColor: getRoleConfig(row.role).bgColor,
                                color: getRoleConfig(row.role).color
                            }} />
                        )} />
                        <Column header="Amount" body={(row) => (
                            <span className="font-bold text-green-600">
                                {formatCurrency(row.commission_fixed_amount || 0)}
                            </span>
                        )} />
                    </DataTable>
                )}

                {model === 'milestone_based' && collaboration?.milestones && (
                    <div>
                        <div className="text-900 font-medium mb-3">Milestones</div>
                        <Timeline
                            value={collaboration.milestones}
                            opposite={(item) => (
                                <span className="font-bold text-green-600">{formatCurrency(item.amount)}</span>
                            )}
                            content={(item) => (
                                <div>
                                    <div className="font-medium">{item.name}</div>
                                    <div className="text-sm text-500">
                                        {item.completed ? (
                                            <span className="text-green-600">
                                                <i className="pi pi-check mr-1" />
                                                Completed {item.completed_at && formatTimeAgo(item.completed_at)}
                                            </span>
                                        ) : (
                                            <span className="text-orange-500">Pending</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            marker={(item) => (
                                <span className={`flex align-items-center justify-content-center border-circle ${
                                    item.completed ? 'bg-green-500' : 'bg-gray-300'
                                }`} style={{ width: '24px', height: '24px' }}>
                                    <i className={`pi ${item.completed ? 'pi-check' : 'pi-circle'} text-white text-xs`} />
                                </span>
                            )}
                        />
                    </div>
                )}
            </Card>
        );
    };

    // Render team members section
    const renderTeamSection = () => (
        <Card className="mb-4">
            <div className="flex align-items-center justify-content-between mb-4">
                <h3 className="text-900 font-semibold m-0">
                    <i className="pi pi-users text-primary mr-2" />
                    Lead Deal Team
                    <Badge value={members.length} className="ml-2" />
                </h3>
                {isOwner() && isActive() && (
                    <Button
                        label="Invite Member"
                        icon="pi pi-user-plus"
                        className="p-button-sm"
                        onClick={() => navigate(`/app/collaborations/${id}/invite`)}
                    />
                )}
            </div>

            <div className="grid">
                {members.map((member, idx) => {
                    const roleConfig = getRoleConfig(member.role);
                    const statusConfig = getMemberStatusConfig(member.status);
                    const contributionConfig = getContributionLevelConfig(member.contribution_level);
                    const isMe = member.profile_id === collaboration?.my_profile_id;

                    return (
                        <div key={idx} className="col-12 md:col-6 lg:col-4">
                            <div className={`surface-100 border-round p-3 h-full ${isMe ? 'border-2 border-primary' : ''}`}>
                                <div className="flex align-items-start justify-content-between mb-3">
                                    <div className="flex align-items-center gap-3">
                                        <Avatar
                                            label={member.name?.charAt(0) || 'U'}
                                            size="large"
                                            shape="circle"
                                            style={{
                                                backgroundColor: roleConfig.bgColor,
                                                color: roleConfig.color
                                            }}
                                        />
                                        <div>
                                            <div className="font-semibold text-900 flex align-items-center gap-2">
                                                {member.name}
                                                {isMe && <Tag value="You" style={{ ...outlineTagStyle('info') }} className="text-xs" />}
                                            </div>
                                            <Tag
                                                value={roleConfig.label}
                                                icon={`pi ${roleConfig.icon}`}
                                                style={{
                                                    backgroundColor: roleConfig.bgColor,
                                                    color: roleConfig.color,
                                                    border: 'none'
                                                }}
                                                className="text-xs mt-1"
                                            />
                                        </div>
                                    </div>
                                    {isOwner() && !isMe && isActive() && member.status === 'active' && (
                                        <div className="flex gap-1">
                                            <Button
                                                icon="pi pi-pencil"
                                                className="p-button-rounded p-button-text p-button-sm"
                                                tooltip="Edit"
                                                onClick={() => navigate(`/app/collaborations/${id}/member/${member.id}/edit`)}
                                            />
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                tooltip="Remove"
                                                onClick={() => handleRemoveMember(member)}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="grid">
                                    <div className="col-6">
                                        <div className="text-500 text-xs mb-1">Commission</div>
                                        <div className="font-bold text-green-600">
                                            {collaboration?.commission_model === 'percentage_split'
                                                ? `${member.commission_percentage || 0}%`
                                                : formatCurrency(member.commission_fixed_amount || 0)
                                            }
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-500 text-xs mb-1">Status</div>
                                        <Tag
                                            value={statusConfig.label}
                                            icon={`pi ${statusConfig.icon}`}
                                            style={{ color: statusConfig.color }}
                                            className="text-xs bg-transparent p-0"
                                        />
                                    </div>
                                </div>

                                {member.contribution_notes && (
                                    <div className="mt-2 pt-2 border-top-1 border-200">
                                        <div className="text-500 text-xs mb-1">Notes</div>
                                        <div className="text-700 text-sm line-height-3">{member.contribution_notes}</div>
                                    </div>
                                )}

                                <div className="mt-2 text-500 text-xs">
                                    <i className="pi pi-clock mr-1" />
                                    Joined {formatTimeAgo(member.joined_at || member.created_at)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );

    // Render activity timeline - Compact
    const renderActivityTimeline = () => (
        <Card className="mb-2 p-2">
            <h3 className="text-900 font-semibold m-0 mb-2" style={{ fontSize: '0.85rem' }}>
                <i className="pi pi-history text-primary mr-1" style={{ fontSize: '0.75rem' }} />
                Activity Timeline
            </h3>

            {activities.length === 0 ? (
                <div className="text-center text-500 py-3">
                    <i className="pi pi-info-circle mb-1" style={{ fontSize: '1.25rem' }} />
                    <div style={{ fontSize: '0.75rem' }}>No activity recorded yet</div>
                </div>
            ) : (
                <Timeline
                    value={activities}
                    opposite={(item) => (
                        <span className="text-500" style={{ fontSize: '0.6rem' }}>{formatTimeAgo(item.created_at)}</span>
                    )}
                    content={(item) => (
                        <div>
                            <div className="font-medium text-900" style={{ fontSize: '0.75rem' }}>{formatActivityText(item)}</div>
                            {item.comment && (
                                <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>"{item.comment}"</div>
                            )}
                        </div>
                    )}
                    marker={(item) => (
                        <span className="flex align-items-center justify-content-center bg-primary border-circle"
                              style={{ width: '20px', height: '20px' }}>
                            <i className={`pi ${getActivityIcon(item.action_type)} text-white`} style={{ fontSize: '0.55rem' }} />
                        </span>
                    )}
                />
            )}
        </Card>
    );

    // Render commission preview panel
    const renderPreviewPanel = () => (
        <Card className="mb-4">
            <h3 className="text-900 font-semibold m-0 mb-4">
                <i className="pi pi-calculator text-primary mr-2" />
                Commission Calculator
            </h3>

            <div className="field mb-4">
                <label className="block text-900 font-medium mb-2">Loan Amount Disbursed</label>
                <InputNumber
                    value={previewData.loan_amount_disbursed}
                    onValueChange={(e) => setPreviewData({ ...previewData, loan_amount_disbursed: e.value })}
                    mode="currency"
                    currency="INR"
                    locale="en-IN"
                    className="w-full"
                />
            </div>

            <div className="field mb-4">
                <label className="block text-900 font-medium mb-2">Total Commission</label>
                <InputNumber
                    value={previewData.total_commission}
                    onValueChange={(e) => setPreviewData({ ...previewData, total_commission: e.value })}
                    mode="currency"
                    currency="INR"
                    locale="en-IN"
                    className="w-full"
                />
            </div>

            <Button
                label="Calculate Distribution"
                icon="pi pi-calculator"
                className="w-full mb-4"
                onClick={handlePreviewCommission}
                loading={previewLoading}
            />

            {previewResult && (
                <div className="surface-100 border-round p-3">
                    <div className="text-900 font-medium mb-3">Distribution Preview</div>
                    {previewResult.distributions?.map((dist, idx) => (
                        <div key={idx} className="flex justify-content-between align-items-center mb-2">
                            <div className="flex align-items-center gap-2">
                                <span>{dist.name}</span>
                                {dist.is_you && <Tag value="You" style={{ ...outlineTagStyle('info') }} className="text-xs" />}
                            </div>
                            <span className={`font-bold ${dist.is_you ? 'text-green-600 text-lg' : ''}`}>
                                {formatCurrency(dist.amount)}
                            </span>
                        </div>
                    ))}
                    <Divider />
                    <div className="flex justify-content-between font-bold">
                        <span>Total</span>
                        <span className="text-green-600">{formatCurrency(previewResult.total || 0)}</span>
                    </div>
                </div>
            )}

            {isOwner() && isActive() && (
                <Button
                    label="Complete Lead Deal"
                    icon="pi pi-check-circle"
                    className="w-full mt-4 p-button-success"
                    onClick={() => setShowCompleteDialog(true)}
                />
            )}
        </Card>
    );

    // Loading skeleton
    if (loading) {
        return (
            <div className="collaboration-details">
                <div className="surface-card border-round shadow-1 p-4 mb-4">
                    <div className="flex align-items-center gap-3">
                        <Skeleton shape="circle" size="40px" />
                        <div className="flex-1">
                            <Skeleton width="300px" height="1.5rem" className="mb-2" />
                            <Skeleton width="200px" height="1rem" />
                        </div>
                        <Skeleton width="100px" height="36px" />
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12 lg:col-8">
                        <Skeleton height="300px" className="mb-4" />
                        <Skeleton height="250px" className="mb-4" />
                        <Skeleton height="200px" />
                    </div>
                    <div className="col-12 lg:col-4">
                        <Skeleton height="400px" />
                    </div>
                </div>
            </div>
        );
    }

    if (!collaboration) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-8">
                <i className="pi pi-exclamation-circle text-5xl text-500 mb-4" />
                <h3 className="text-900">Lead Deal Not Found</h3>
                <Button label="Back to Lead Deals" icon="pi pi-arrow-left" onClick={() => navigate('/app/collaborations')} />
            </div>
        );
    }

    const statusConfig = getStatusConfig(collaboration.status);

    return (
        <div className="collaboration-details">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Breadcrumb Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between flex-wrap gap-1">
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-arrow-left"
                            className="p-button-text p-button-rounded"
                            style={{ padding: '0.25rem', fontSize: '0.75rem' }}
                            onClick={() => navigate('/app/collaborations')}
                            tooltip="Back"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                        <span className="text-500 hidden md:inline" style={{ fontSize: '0.7rem' }}>Lead Deals</span>
                        <i className="pi pi-angle-right text-400 hidden md:inline" style={{ fontSize: '0.65rem' }} />
                        <span className="text-900 font-medium" style={{ fontSize: '0.8rem' }}>
                            <span className="hidden md:inline">Lead Details</span>
                            <span className="inline md:hidden">#{collaboration?.id?.slice(-8)}</span>
                        </span>
                    </div>
                    <div className="flex align-items-center gap-1">
                        <Tag
                            value={statusConfig.label}
                            icon={`pi ${statusConfig.icon}`}
                            style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.65rem', padding: '2px 6px' }}
                        />
                        <Tag
                            value={getRoleConfig(collaboration.my_role).label}
                            style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                        />
                        {isActive() && (
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-outlined hidden md:flex"
                                style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                                onClick={openEditLeadDialog}
                                tooltip="Edit Lead"
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Action Bar - Visible only on mobile */}
            <div className="surface-card border-round shadow-1 p-2 mb-2 flex md:hidden align-items-center justify-content-between gap-1">
                <div className="flex align-items-center gap-1 flex-1 overflow-x-auto">
                    {isActive() && (
                        <Button
                            icon="pi pi-pencil"
                            label="Edit"
                            className="p-button-outlined p-button-sm flex-shrink-0"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={openEditLeadDialog}
                        />
                    )}
                    {isOwner() && isActive() && (
                        <>
                            <Button
                                icon="pi pi-check-circle"
                                label="Complete"
                                className="p-button-success p-button-sm flex-shrink-0"
                                style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                onClick={() => setShowCompleteDialog(true)}
                            />
                            <Button
                                icon="pi pi-times-circle"
                                label="Cancel"
                                className="p-button-danger p-button-outlined p-button-sm flex-shrink-0"
                                style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                onClick={() => setShowCancelDialog(true)}
                            />
                        </>
                    )}
                    {!isOwner() && isActive() && (
                        <Button
                            icon="pi pi-sign-out"
                            label="Leave"
                            className="p-button-warning p-button-outlined p-button-sm flex-shrink-0"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={() => setShowLeaveDialog(true)}
                        />
                    )}
                    {isActive() && (
                        <Button
                            icon="pi pi-exclamation-triangle"
                            className="p-button-text p-button-secondary p-button-sm flex-shrink-0"
                            style={{ fontSize: '0.65rem', padding: '0.25rem' }}
                            onClick={() => setShowDisputeDialog(true)}
                            tooltip="Dispute"
                        />
                    )}
                </div>
                {collaboration?.marketplace_lead_id && (
                    <Button
                        icon="pi pi-external-link"
                        className="p-button-text p-button-sm flex-shrink-0"
                        style={{ fontSize: '0.65rem', padding: '0.25rem' }}
                        onClick={() => navigate(`/app/marketplace/listing/${collaboration.marketplace_lead_id}`)}
                        tooltip="Marketplace"
                    />
                )}
            </div>

            {/* Main Content */}
            <div className="grid">
                {/* Left Column - Main Content */}
                <div className="col-12 lg:col-8">
                    {/* Lead Overview Card with Quick Stats, Borrower Info, Lead Details */}
                    {renderLeadCard()}

                    {/* Lead Quality Assessment */}
                    {renderQualityAssessment()}

                    {/* Lead Status Section */}
                    {renderLeadStatus()}

                    {/* Employment & Financial Details */}
                    {renderEmploymentDetails()}

                    {/* Activity Timeline */}
                    {renderActivityTimeline()}
                </div>

                {/* Right Column - Sidebar */}
                <div className="col-12 lg:col-4">
                    {/* Commission Structure */}
                    {renderCommissionCard()}

                    {/* Team Members (Compact) */}
                    {renderSidebarTeamMembers()}

                    {/* Action Card (Sticky) */}
                    {renderSidebarActions()}
                </div>
            </div>

            {/* Documents & Notes Section - Compact */}
            <Card className="mt-2 p-2">
                <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} className="compact-tabview">
                    <TabPanel header={<span style={{ fontSize: '0.75rem' }}><i className="pi pi-folder mr-1" style={{ fontSize: '0.7rem' }} />Documents {documents.length > 0 && <Badge value={documents.length} className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} />}</span>}>
                        {/* Dynamic Document Section */}
                        <MarketplaceDocumentsSection
                            resolvedDocs={resolvedDocs}
                            groupedDocs={groupedDocs}
                            loadingDocMapping={loadingDocMapping}
                            uploadedDocs={uploadedDocs}
                            uploadingDocs={uploadingDocs}
                            uploadProgress={uploadProgress}
                            getUploadedFilesForDefinition={getUploadedFilesForDefinition}
                            handleDocumentSelect={handleDocumentSelect}
                            handleDocumentRemove={handleDocumentRemove}
                            canUpload={isActive()}
                            isOwner={isOwner()}
                            currentProfileId={collaboration?.my_profile_id}
                            readOnly={false}
                        />
                    </TabPanel>

                    <TabPanel header={<span style={{ fontSize: '0.75rem' }}><i className="pi pi-comments mr-1" style={{ fontSize: '0.7rem' }} />Notes {notes.length > 0 && <Badge value={notes.length} className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} />}</span>}>
                        <div className="flex justify-content-between align-items-center mb-2">
                            <h4 className="m-0 text-900" style={{ fontSize: '0.85rem' }}>Team Notes</h4>
                            {isActive() && (
                                <Button
                                    label="Add"
                                    icon="pi pi-plus"
                                    className="p-button-outlined"
                                    style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                                    onClick={() => setShowNoteDialog(true)}
                                />
                            )}
                        </div>

                        {notesLoading ? (
                            <div className="flex justify-content-center py-3">
                                <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                            </div>
                        ) : notes.length === 0 ? (
                            <div className="text-center py-4">
                                <i className="pi pi-comment text-300 mb-2" style={{ display: 'block', fontSize: '2rem' }} />
                                <p className="text-500 m-0" style={{ fontSize: '0.75rem' }}>No notes added yet</p>
                                {isActive() && (
                                    <Button label="Add First Note" icon="pi pi-plus" className="p-button-text mt-2" style={{ fontSize: '0.7rem' }} onClick={() => setShowNoteDialog(true)} />
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-column gap-2">
                                {notes.map((note) => (
                                    <div key={note.id} className={`surface-100 border-round p-2 ${note.is_pinned ? 'border-left-2 border-primary' : ''}`}>
                                        <div className="flex align-items-start justify-content-between mb-1">
                                            <div className="flex align-items-center gap-1">
                                                {note.author?.profile_photo ? (
                                                    <Avatar image={note.author.profile_photo} shape="circle" style={{ width: '24px', height: '24px' }} />
                                                ) : (
                                                    <Avatar icon="pi pi-user" shape="circle" style={{ width: '24px', height: '24px', fontSize: '0.6rem' }} />
                                                )}
                                                <div>
                                                    <div className="font-medium text-900" style={{ fontSize: '0.75rem' }}>{note.author?.name || 'Unknown'}</div>
                                                    <div className="text-500" style={{ fontSize: '0.6rem' }}>{note.time_ago || formatTimeAgo(note.created_at)}</div>
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                {note.is_pinned && <i className="pi pi-bookmark-fill text-primary" style={{ fontSize: '0.65rem' }} />}
                                                {isActive() && (
                                                    <>
                                                        <Button
                                                            icon={note.is_pinned ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'}
                                                            className="p-button-text"
                                                            style={{ padding: '0.15rem', fontSize: '0.6rem' }}
                                                            onClick={() => handleToggleNotePin(note.id)}
                                                            tooltip={note.is_pinned ? 'Unpin' : 'Pin'}
                                                        />
                                                        <Button
                                                            icon="pi pi-trash"
                                                            className="p-button-text p-button-danger"
                                                            style={{ padding: '0.15rem', fontSize: '0.6rem' }}
                                                            onClick={() => handleDeleteNote(note.id)}
                                                            tooltip="Delete"
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <p className="m-0 text-700 line-height-3 white-space-pre-wrap" style={{ fontSize: '0.75rem' }}>{note.content}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabPanel>

                    <TabPanel header={<span style={{ fontSize: '0.75rem' }}><i className="pi pi-history mr-1" style={{ fontSize: '0.7rem' }} />History {isOwner() && allLeadInterests.length > 0 ? <Badge value={allLeadInterests.length} className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} /> : negotiations.length > 0 && <Badge value={negotiations.length} className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} />}</span>}>
                        <div className="mb-2">
                            <h4 className="m-0 text-900 mb-1" style={{ fontSize: '0.85rem' }}>
                                {isOwner() ? 'All Interest Negotiations' : 'Commission Negotiation Timeline'}
                            </h4>
                            <p className="text-500 m-0" style={{ fontSize: '0.7rem' }}>
                                {isOwner()
                                    ? 'View all agents who expressed interest'
                                    : 'History of how this lead deal was negotiated'
                                }
                            </p>
                        </div>

                        {negotiationsLoading ? (
                            <div className="flex justify-content-center py-2">
                                <ProgressSpinner style={{ width: '28px', height: '28px' }} />
                            </div>
                        ) : isOwner() && allLeadInterests.length > 0 ? (
                            /* OWNER VIEW: Show all interests on this lead */
                            <div className="flex flex-column gap-2">
                                {/* Summary Stats - Compact */}
                                <div className="surface-100 border-round p-2 mb-1">
                                    <div className="flex flex-wrap gap-3">
                                        <div className="flex align-items-center gap-1">
                                            <i className="pi pi-users text-primary" style={{ fontSize: '0.7rem' }} />
                                            <span className="text-600" style={{ fontSize: '0.65rem' }}>Total:</span>
                                            <span className="font-bold text-900" style={{ fontSize: '0.75rem' }}>{allLeadInterests.length}</span>
                                        </div>
                                        <div className="flex align-items-center gap-1">
                                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.7rem' }} />
                                            <span className="text-600" style={{ fontSize: '0.65rem' }}>Accepted:</span>
                                            <span className="font-bold text-green-600" style={{ fontSize: '0.75rem' }}>{allLeadInterests.filter(i => i.status === 'accepted').length}</span>
                                        </div>
                                        <div className="flex align-items-center gap-1">
                                            <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.7rem' }} />
                                            <span className="text-600" style={{ fontSize: '0.65rem' }}>Rejected:</span>
                                            <span className="font-bold text-red-600" style={{ fontSize: '0.75rem' }}>{allLeadInterests.filter(i => i.status === 'rejected').length}</span>
                                        </div>
                                        <div className="flex align-items-center gap-1">
                                            <i className="pi pi-clock text-orange-500" style={{ fontSize: '0.7rem' }} />
                                            <span className="text-600" style={{ fontSize: '0.65rem' }}>Pending:</span>
                                            <span className="font-bold text-orange-600" style={{ fontSize: '0.75rem' }}>{allLeadInterests.filter(i => i.status === 'pending' || i.status === 'countered').length}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Interest Cards - Compact */}
                                {allLeadInterests.map((interest) => {
                                    const statusBadge = getInterestStatusBadge(interest.status);
                                    const isExpanded = expandedInterests[interest.id];
                                    const isCurrentCollaboration = interest.id === (collaboration?.marketplace_interest_id || collaboration?.interest_id);

                                    return (
                                        <div
                                            key={interest.id}
                                            className={`surface-card border-round shadow-1 overflow-hidden ${isCurrentCollaboration ? 'border-2 border-green-500' : 'border-1 border-200'}`}
                                        >
                                            {/* Interest Header - Clickable to expand */}
                                            <div
                                                className="p-2 cursor-pointer hover:surface-100 transition-colors transition-duration-200"
                                                onClick={() => toggleInterestExpansion(interest.id)}
                                            >
                                                <div className="flex align-items-center justify-content-between">
                                                    <div className="flex align-items-center gap-2">
                                                        <Avatar
                                                            image={interest.interested_agent?.profile_photo}
                                                            icon="pi pi-user"
                                                            shape="circle"
                                                            className="bg-primary"
                                                            style={{ width: '28px', height: '28px', fontSize: '0.65rem' }}
                                                        />
                                                        <div>
                                                            <div className="flex align-items-center gap-1">
                                                                <span className="font-bold text-900" style={{ fontSize: '0.75rem' }}>
                                                                    {interest.interested_agent?.name || interest.interested_agent?.full_name || 'Agent'}
                                                                </span>
                                                                {isCurrentCollaboration && (
                                                                    <Tag value="Current" style={{ ...outlineTagStyle('success'), fontSize: '0.5rem', padding: '1px 3px' }} />
                                                                )}
                                                            </div>
                                                            <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                                                {interest.interested_agent?.company || interest.interested_agent?.current_company || 'No company'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex align-items-center gap-2">
                                                        <div className="text-right">
                                                            <Tag value={statusBadge.label} icon={statusBadge.icon} style={{ ...outlineTagStyle(statusBadge.severity), fontSize: '0.55rem', padding: '1px 4px' }} />
                                                            <div className="text-500 mt-1" style={{ fontSize: '0.55rem' }}>{formatTimeAgo(interest.expressed_at || interest.created_at)}</div>
                                                        </div>
                                                        <div className="flex flex-column align-items-center">
                                                            <span className="font-bold text-primary" style={{ fontSize: '1rem' }}>{interest.final_percentage || interest.proposed_percentage}%</span>
                                                            <span className="text-500" style={{ fontSize: '0.5rem' }}>Commission</span>
                                                        </div>
                                                        <Button
                                                            icon={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                                                            className="p-button-text p-button-rounded"
                                                            style={{ padding: '0.2rem', fontSize: '0.65rem' }}
                                                            onClick={(e) => { e.stopPropagation(); toggleInterestExpansion(interest.id); }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Quick Info Row - Compact */}
                                                <div className="flex flex-wrap gap-2 mt-2 pt-1 border-top-1 border-200">
                                                    <div className="flex align-items-center gap-1">
                                                        <i className="pi pi-send text-blue-500" style={{ fontSize: '0.6rem' }} />
                                                        <span className="text-600" style={{ fontSize: '0.6rem' }}>Proposed:</span>
                                                        <span className="font-semibold" style={{ fontSize: '0.65rem' }}>{interest.proposed_percentage}%</span>
                                                    </div>
                                                    {interest.counter_percentage && (
                                                        <div className="flex align-items-center gap-1">
                                                            <i className="pi pi-refresh text-orange-500" style={{ fontSize: '0.6rem' }} />
                                                            <span className="text-600" style={{ fontSize: '0.6rem' }}>Counter:</span>
                                                            <span className="font-semibold" style={{ fontSize: '0.65rem' }}>{interest.counter_percentage}%</span>
                                                        </div>
                                                    )}
                                                    <div className="flex align-items-center gap-1">
                                                        <i className="pi pi-sync text-purple-500" style={{ fontSize: '0.6rem' }} />
                                                        <span className="text-600" style={{ fontSize: '0.6rem' }}>Rounds:</span>
                                                        <span className="font-semibold" style={{ fontSize: '0.65rem' }}>{interest.negotiation_round || 1}</span>
                                                    </div>
                                                    <div className="flex align-items-center gap-1 capitalize">
                                                        <i className="pi pi-briefcase text-gray-500" style={{ fontSize: '0.6rem' }} />
                                                        <span className="text-600" style={{ fontSize: '0.6rem' }}>Role:</span>
                                                        <span className="font-semibold" style={{ fontSize: '0.65rem' }}>{interest.requested_role || 'collaborator'}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded Content - Negotiation Timeline - Compact */}
                                            {isExpanded && (
                                                <div className="border-top-1 border-200 p-2 bg-gray-50">
                                                    {interest.interest_message && (
                                                        <div className="surface-0 border-round p-2 mb-2">
                                                            <div className="flex align-items-center gap-1 mb-1">
                                                                <i className="pi pi-comment text-primary" style={{ fontSize: '0.65rem' }} />
                                                                <span className="font-semibold text-900" style={{ fontSize: '0.7rem' }}>Interest Message</span>
                                                            </div>
                                                            <p className="m-0 text-700 font-italic" style={{ fontSize: '0.7rem' }}>"{interest.interest_message}"</p>
                                                        </div>
                                                    )}

                                                    {interest.rejection_reason && (
                                                        <div className="surface-0 border-round p-2 mb-2 border-left-2 border-red-500">
                                                            <div className="flex align-items-center gap-1 mb-1">
                                                                <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.65rem' }} />
                                                                <span className="font-semibold text-red-700" style={{ fontSize: '0.7rem' }}>Rejection Reason</span>
                                                            </div>
                                                            <p className="m-0 text-700" style={{ fontSize: '0.7rem' }}>{interest.rejection_reason}</p>
                                                        </div>
                                                    )}

                                                    <div className="text-900 mb-2 flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                                                        <i className="pi pi-history text-primary" style={{ fontSize: '0.65rem' }} />
                                                        <span className="font-semibold">Negotiation Timeline</span>
                                                    </div>

                                                    {interest.negotiations && interest.negotiations.length > 0 ? (
                                                        <Timeline
                                                            value={interest.negotiations}
                                                            align="left"
                                                            className="customized-timeline pl-0"
                                                            marker={(item) => (
                                                                <span
                                                                    className="flex align-items-center justify-content-center border-circle"
                                                                    style={{
                                                                        width: '1.25rem',
                                                                        height: '1.25rem',
                                                                        backgroundColor:
                                                                            item.action_type === 'accepted' ? 'var(--green-500)' :
                                                                            item.action_type === 'rejected' ? 'var(--red-500)' :
                                                                            item.action_type === 'counter_offer' ? 'var(--orange-500)' :
                                                                            item.action_type === 'interest_expressed' ? 'var(--blue-500)' :
                                                                            item.action_type === 'new_proposal' ? 'var(--purple-500)' :
                                                                            'var(--gray-500)'
                                                                    }}
                                                                >
                                                                    <i className={`text-white ${
                                                                        item.action_type === 'accepted' ? 'pi pi-check' :
                                                                        item.action_type === 'rejected' ? 'pi pi-times' :
                                                                        item.action_type === 'counter_offer' ? 'pi pi-refresh' :
                                                                        item.action_type === 'interest_expressed' ? 'pi pi-star' :
                                                                        item.action_type === 'new_proposal' ? 'pi pi-send' :
                                                                        'pi pi-circle'
                                                                    }`} style={{ fontSize: '0.5rem' }} />
                                                                </span>
                                                            )}
                                                            content={(item) => (
                                                                <div className="surface-0 border-round p-1 mb-1 shadow-1">
                                                                    <div className="flex align-items-center justify-content-between">
                                                                        <Tag
                                                                            value={item.label || item.action_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                            style={{ ...outlineTagStyle(
                                                                                item.action_type === 'accepted' ? 'success' :
                                                                                item.action_type === 'rejected' ? 'danger' :
                                                                                item.action_type === 'counter_offer' ? 'warning' :
                                                                                'info'
                                                                            ), fontSize: '0.5rem', padding: '1px 3px' }}
                                                                        />
                                                                        <span className="text-400" style={{ fontSize: '0.55rem' }}>{item.time_ago || formatTimeAgo(item.created_at)}</span>
                                                                    </div>
                                                                    <div className="flex align-items-center gap-1 mt-1" style={{ fontSize: '0.65rem' }}>
                                                                        <span className="text-600 capitalize">{item.actor_role}:</span>
                                                                        <span className="text-900">{item.actor?.name || (item.actor_role === 'owner' ? 'You' : 'Agent')}</span>
                                                                        {item.proposed_percentage && (
                                                                            <span className="text-primary font-bold">→ {item.proposed_percentage}%</span>
                                                                        )}
                                                                    </div>
                                                                    {item.message && (
                                                                        <div className="text-600 mt-1 font-italic" style={{ fontSize: '0.6rem' }}>"{item.message}"</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        />
                                                    ) : (
                                                        <div className="text-center py-2 text-500" style={{ fontSize: '0.7rem' }}>
                                                            <i className="pi pi-spin pi-spinner mr-1" style={{ fontSize: '0.65rem' }} />
                                                            Loading...
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : negotiations.length === 0 ? (
                            /* NO NEGOTIATIONS - Compact */
                            <div className="text-center py-3">
                                <i className="pi pi-history text-300 mb-2" style={{ display: 'block', fontSize: '1.5rem' }} />
                                <p className="text-500 m-0" style={{ fontSize: '0.75rem' }}>No negotiation history available</p>
                            </div>
                        ) : (
                            /* COLLABORATOR VIEW: Show only their negotiation - Compact */
                            <>
                                {/* Final Agreement Summary - Compact */}
                                {interestDetails && (
                                    <div className="surface-100 border-round p-2 mb-2">
                                        <div className="flex align-items-center gap-1 mb-2">
                                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.8rem' }} />
                                            <span className="font-bold text-900" style={{ fontSize: '0.75rem' }}>Final Agreement</span>
                                        </div>
                                        <div className="grid">
                                            <div className="col-6 md:col-3 p-1">
                                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Initial Proposal</div>
                                                <div className="font-bold text-primary" style={{ fontSize: '0.85rem' }}>{interestDetails.proposed_percentage}%</div>
                                            </div>
                                            {interestDetails.counter_percentage && (
                                                <div className="col-6 md:col-3 p-1">
                                                    <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Counter Offer</div>
                                                    <div className="font-bold text-orange-500" style={{ fontSize: '0.85rem' }}>{interestDetails.counter_percentage}%</div>
                                                </div>
                                            )}
                                            <div className="col-6 md:col-3 p-1">
                                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Final Commission</div>
                                                <div className="font-bold text-green-600" style={{ fontSize: '0.85rem' }}>{interestDetails.final_percentage || interestDetails.proposed_percentage}%</div>
                                            </div>
                                            <div className="col-6 md:col-3 p-1">
                                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Negotiation Rounds</div>
                                                <div className="font-bold" style={{ fontSize: '0.85rem' }}>{interestDetails.negotiation_round || 1}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Negotiation Timeline for Collaborator - Compact */}
                                <Timeline
                                    value={negotiations}
                                    align="alternate"
                                    className="customized-timeline compact-timeline"
                                    marker={(item) => (
                                        <span
                                            className="flex align-items-center justify-content-center border-circle shadow-1"
                                            style={{
                                                width: '1.5rem',
                                                height: '1.5rem',
                                                backgroundColor:
                                                    item.action_type === 'accepted' ? 'var(--green-500)' :
                                                    item.action_type === 'rejected' ? 'var(--red-500)' :
                                                    item.action_type === 'counter_offer' ? 'var(--orange-500)' :
                                                    item.action_type === 'interest_expressed' ? 'var(--blue-500)' :
                                                    item.action_type === 'new_proposal' ? 'var(--purple-500)' :
                                                    item.action_type === 'withdrawn' ? 'var(--gray-500)' :
                                                    'var(--primary-color)'
                                            }}
                                        >
                                            <i className={`text-white ${
                                                item.action_type === 'accepted' ? 'pi pi-check' :
                                                item.action_type === 'rejected' ? 'pi pi-times' :
                                                item.action_type === 'counter_offer' ? 'pi pi-refresh' :
                                                item.action_type === 'interest_expressed' ? 'pi pi-star' :
                                                item.action_type === 'new_proposal' ? 'pi pi-send' :
                                                item.action_type === 'withdrawn' ? 'pi pi-undo' :
                                                'pi pi-circle'
                                            }`} style={{ fontSize: '0.55rem' }} />
                                        </span>
                                    )}
                                    content={(item) => (
                                        <div className="surface-card shadow-1 border-round p-2">
                                            <div className="flex align-items-center justify-content-between mb-1">
                                                <Tag
                                                    value={item.label || item.action_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    style={{ ...outlineTagStyle(
                                                        item.action_type === 'accepted' ? 'success' :
                                                        item.action_type === 'rejected' ? 'danger' :
                                                        item.action_type === 'counter_offer' ? 'warning' :
                                                        item.action_type === 'interest_expressed' ? 'info' :
                                                        item.action_type === 'new_proposal' ? 'info' :
                                                        'secondary'
                                                    ), fontSize: '0.55rem', padding: '1px 4px' }}
                                                />
                                                <span className="text-500" style={{ fontSize: '0.55rem' }}>{item.time_ago || formatTimeAgo(item.created_at)}</span>
                                            </div>
                                            <div className="flex align-items-center gap-1 mb-1">
                                                <Avatar
                                                    icon="pi pi-user"
                                                    shape="circle"
                                                    className="bg-primary"
                                                    style={{ width: '20px', height: '20px', fontSize: '0.5rem' }}
                                                />
                                                <div>
                                                    <div className="font-medium text-900" style={{ fontSize: '0.7rem' }}>
                                                        {item.actor?.name || item.actor_name || (item.actor_role === 'owner' ? 'Lead Owner' : 'You')}
                                                    </div>
                                                    <div className="text-500 capitalize" style={{ fontSize: '0.55rem' }}>{item.actor_role}</div>
                                                </div>
                                            </div>
                                            {item.proposed_percentage && (
                                                <div className="flex align-items-center gap-1 mb-1">
                                                    <i className="pi pi-percentage text-primary" style={{ fontSize: '0.6rem' }} />
                                                    <span className="font-bold text-primary" style={{ fontSize: '0.7rem' }}>{item.proposed_percentage}%</span>
                                                    <span className="text-500" style={{ fontSize: '0.6rem' }}>commission</span>
                                                </div>
                                            )}
                                            {item.message && (
                                                <div className="surface-100 border-round p-1 mt-1">
                                                    <p className="m-0 text-700 line-height-3 font-italic" style={{ fontSize: '0.65rem' }}>"{item.message}"</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </>
                        )}
                    </TabPanel>

                    {/* Module 7: Conversion Tab */}
                    <TabPanel header={<span style={{ fontSize: '0.75rem' }}><i className="pi pi-check-circle mr-1" style={{ fontSize: '0.7rem' }} />Conversion</span>}>
                        <ConversionTab
                            collaborationId={id}
                            collaboration={collaboration}
                            isOwner={isOwner()}
                            isCollaborator={!isOwner()}
                            onRefresh={fetchCollaboration}
                            toast={toast}
                        />
                    </TabPanel>
                </TabView>
            </Card>

            {/* Add Note Dialog */}
            <Dialog
                visible={showNoteDialog}
                onHide={() => setShowNoteDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-comment text-primary" /><span>Add Note</span></div>}
                style={{ width: '500px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowNoteDialog(false)} />
                        <Button label="Add Note" icon="pi pi-check" onClick={handleAddNote} loading={processing} disabled={!noteData.content.trim()} />
                    </div>
                }
            >
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Note Content *</label>
                    <InputTextarea
                        value={noteData.content}
                        onChange={(e) => setNoteData({ ...noteData, content: e.target.value })}
                        rows={4}
                        className="w-full"
                        placeholder="Add your note here..."
                        autoFocus
                    />
                </div>
                <div className="field-checkbox">
                    <Checkbox
                        inputId="pinNote"
                        checked={noteData.is_pinned}
                        onChange={(e) => setNoteData({ ...noteData, is_pinned: e.checked })}
                    />
                    <label htmlFor="pinNote" className="ml-2">Pin this note</label>
                </div>
            </Dialog>

            {/* Document Full Size Modal */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className={getDocumentIcon(selectedDocumentForModal)} />
                        <span>{selectedDocumentForModal?.document_name || selectedDocumentForModal?.file_name || 'Document Viewer'}</span>
                    </div>
                }
                visible={documentModalVisible}
                onHide={() => {
                    setDocumentModalVisible(false);
                    setSelectedDocumentForModal(null);
                }}
                style={{ width: '90vw', maxWidth: '1200px' }}
                maximizable
                modal
            >
                {selectedDocumentForModal && (
                    <div className="flex flex-column">
                        {/* Document Actions */}
                        <div className="flex justify-content-between align-items-center mb-3 pb-3 border-bottom-1 surface-border">
                            <div className="flex align-items-center gap-2">
                                <i className={`${getDocumentIcon(selectedDocumentForModal)} text-primary`}></i>
                                <span className="font-semibold text-900">{selectedDocumentForModal.document_name || selectedDocumentForModal.file_name}</span>
                                <Tag value={selectedDocumentForModal.category_display || selectedDocumentForModal.category} style={{ ...outlineTagStyle('info') }} />
                                {selectedDocumentForModal.source === 'original' && <Tag value="Original" style={{ ...outlineTagStyle('success') }} />}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    icon="pi pi-download"
                                    label="Download"
                                    className="p-button-outlined"
                                    onClick={() => handleDownloadDocument(selectedDocumentForModal)}
                                />
                                <Button
                                    icon="pi pi-external-link"
                                    label="Open in New Tab"
                                    className="p-button-text"
                                    onClick={() => {
                                        const fileUrl = selectedDocumentForModal?.file_url || selectedDocumentForModal?.file_path;
                                        if (fileUrl) window.open(fileUrl, '_blank');
                                    }}
                                />
                            </div>
                        </div>

                        {/* Document Content */}
                        <div className="text-center" style={{ minHeight: '60vh' }}>
                            {(() => {
                                const fileUrl = selectedDocumentForModal?.file_url || selectedDocumentForModal?.file_path;
                                const isImage = selectedDocumentForModal?.is_image || selectedDocumentForModal?.file_extension?.match(/^(jpg|jpeg|png|gif|bmp|webp)$/i);
                                const isPdf = selectedDocumentForModal?.is_pdf || selectedDocumentForModal?.file_extension?.toLowerCase() === 'pdf';

                                if (fileUrl) {
                                    if (isImage) {
                                        return (
                                            <img
                                                src={fileUrl}
                                                alt={selectedDocumentForModal?.document_name || 'Document'}
                                                className="border-round"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '70vh',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        );
                                    } else if (isPdf) {
                                        return (
                                            <iframe
                                                src={fileUrl}
                                                className="border-round border-none bg-white w-full"
                                                style={{ height: '70vh' }}
                                                title={selectedDocumentForModal?.document_name || 'PDF Document'}
                                            />
                                        );
                                    }
                                }

                                return (
                                    <div className="surface-200 border-round p-6 flex flex-column align-items-center justify-content-center" style={{ height: '60vh' }}>
                                        <i className={`${getDocumentIcon(selectedDocumentForModal)} text-6xl text-primary mb-3`}></i>
                                        <h4 className="text-900 font-bold mb-2">{selectedDocumentForModal?.document_name || 'Document'}</h4>
                                        <p className="text-600">Document preview not available for this file type</p>
                                        <Button
                                            label="Download File"
                                            icon="pi pi-download"
                                            className="mt-3"
                                            onClick={() => handleDownloadDocument(selectedDocumentForModal)}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Complete Dialog */}
            <Dialog
                visible={showCompleteDialog}
                onHide={() => setShowCompleteDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-check-circle text-green-500" /><span>Complete Lead Deal</span></div>}
                style={{ width: '500px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowCompleteDialog(false)} />
                        <Button label="Complete & Distribute" icon="pi pi-check" className="p-button-success" onClick={handleComplete} loading={processing} />
                    </div>
                }
            >
                <div className="bg-green-50 border-round p-3 mb-4">
                    <div className="flex align-items-center gap-2 text-green-700">
                        <i className="pi pi-info-circle" />
                        <span className="text-sm">Commissions will be distributed to all team members upon completion.</span>
                    </div>
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Loan Amount Disbursed *</label>
                    <InputNumber value={completeData.loan_amount_disbursed} onValueChange={(e) => setCompleteData({ ...completeData, loan_amount_disbursed: e.value })} mode="currency" currency="INR" locale="en-IN" className="w-full" />
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Total Commission Earned *</label>
                    <InputNumber value={completeData.total_commission} onValueChange={(e) => setCompleteData({ ...completeData, total_commission: e.value })} mode="currency" currency="INR" locale="en-IN" className="w-full" />
                </div>
                <div className="field">
                    <label className="block text-900 font-medium mb-2">Notes (optional)</label>
                    <InputTextarea value={completeData.notes} onChange={(e) => setCompleteData({ ...completeData, notes: e.target.value })} rows={3} className="w-full" />
                </div>
            </Dialog>

            {/* Cancel Dialog */}
            <Dialog
                visible={showCancelDialog}
                onHide={() => setShowCancelDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-times-circle text-red-500" /><span>Cancel Lead Deal</span></div>}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Keep Active" className="p-button-text" onClick={() => setShowCancelDialog(false)} />
                        <Button label="Cancel Lead Deal" icon="pi pi-times" className="p-button-danger" onClick={handleCancel} loading={processing} />
                    </div>
                }
            >
                <div className="bg-red-50 border-round p-3 mb-4">
                    <div className="flex align-items-center gap-2 text-red-700">
                        <i className="pi pi-exclamation-triangle" />
                        <span className="text-sm">This action cannot be undone. No commissions will be distributed.</span>
                    </div>
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Reason for Cancellation *</label>
                    <Dropdown value={cancelData.reason} options={CANCELLATION_REASONS} onChange={(e) => setCancelData({ ...cancelData, reason: e.value })} optionLabel="label" optionValue="value" placeholder="Select reason" className="w-full" />
                </div>
                <div className="field">
                    <label className="block text-900 font-medium mb-2">Additional Notes</label>
                    <InputTextarea value={cancelData.notes} onChange={(e) => setCancelData({ ...cancelData, notes: e.target.value })} rows={3} className="w-full" />
                </div>
            </Dialog>

            {/* Dispute Dialog */}
            <Dialog
                visible={showDisputeDialog}
                onHide={() => setShowDisputeDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-exclamation-triangle text-orange-500" /><span>Raise Dispute</span></div>}
                style={{ width: '550px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowDisputeDialog(false)} />
                        <Button label="Submit Dispute" icon="pi pi-flag" className="p-button-warning" onClick={handleDispute} loading={processing} disabled={disputeData.description.length < 50} />
                    </div>
                }
            >
                <div className="bg-orange-50 border-round p-3 mb-4">
                    <div className="flex align-items-center gap-2 text-orange-700">
                        <i className="pi pi-info-circle" />
                        <span className="text-sm">Disputes will be reviewed by our team. Provide detailed information.</span>
                    </div>
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Dispute Reason *</label>
                    <Dropdown value={disputeData.reason} options={DISPUTE_REASONS} onChange={(e) => setDisputeData({ ...disputeData, reason: e.value })} optionLabel="label" optionValue="value" placeholder="Select reason" className="w-full" />
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Detailed Description * (min 50 characters)</label>
                    <InputTextarea value={disputeData.description} onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })} rows={4} className="w-full" placeholder="Explain the issue in detail..." />
                    <small className="text-500">{disputeData.description.length}/50 characters minimum</small>
                </div>
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Requested Resolution</label>
                    <Dropdown value={disputeData.requested_resolution} options={DISPUTE_RESOLUTIONS} onChange={(e) => setDisputeData({ ...disputeData, requested_resolution: e.value })} optionLabel="label" optionValue="value" placeholder="What resolution do you seek?" className="w-full" />
                </div>
                <div className="field">
                    <label className="block text-900 font-medium mb-2">Against Member (optional)</label>
                    <Dropdown value={disputeData.against_member_id} options={members.filter(m => m.profile_id !== collaboration?.my_profile_id).map(m => ({ label: m.name, value: m.id }))} onChange={(e) => setDisputeData({ ...disputeData, against_member_id: e.value })} placeholder="Select member (if specific)" className="w-full" showClear />
                </div>
            </Dialog>

            {/* Leave Dialog */}
            <Dialog
                visible={showLeaveDialog}
                onHide={() => setShowLeaveDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-sign-out text-orange-500" /><span>Leave Lead Deal</span></div>}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Stay" className="p-button-text" onClick={() => setShowLeaveDialog(false)} />
                        <Button label="Leave Lead Deal" icon="pi pi-sign-out" className="p-button-warning" onClick={handleLeave} loading={processing} />
                    </div>
                }
            >
                <div className="bg-orange-50 border-round p-3 mb-4">
                    <div className="flex align-items-center gap-2 text-orange-700">
                        <i className="pi pi-exclamation-triangle" />
                        <span className="text-sm">You will forfeit your commission share if you leave.</span>
                    </div>
                </div>
                <div className="field">
                    <label className="block text-900 font-medium mb-2">Reason for Leaving (optional)</label>
                    <InputTextarea value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} rows={3} className="w-full" placeholder="Any feedback or reason..." />
                </div>
            </Dialog>

            {/* MODULE 5C: Edit Lead Dialog */}
            <Dialog
                visible={showEditLeadDialog}
                onHide={() => setShowEditLeadDialog(false)}
                header={<div className="flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}><i className="pi pi-pencil text-primary" style={{ fontSize: '0.8rem' }} /><span className="font-semibold">Edit Lead Information</span></div>}
                style={{ width: '680px' }}
                modal
                className="edit-lead-dialog"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text p-button-sm" style={{ fontSize: '0.75rem' }} onClick={() => setShowEditLeadDialog(false)} />
                        <Button label="Save Changes" icon="pi pi-check" className="p-button-sm" style={{ fontSize: '0.75rem' }} onClick={handleUpdateLead} loading={editLeadProcessing} />
                    </div>
                }
            >
                <Message severity="info" text="Changes will be logged in the activity timeline and visible to all lead deal members." className="mb-3 w-full" style={{ fontSize: '0.7rem' }} />
                <div className="grid" style={{ fontSize: '0.75rem' }}>
                    {/* Personal Information */}
                    <div className="col-12 mb-1">
                        <h4 className="text-primary m-0 p-0" style={{ fontSize: '0.8rem' }}>Personal Information</h4>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Full Name</label>
                            <InputText value={editLeadData.full_name || ''} onChange={(e) => setEditLeadData({...editLeadData, full_name: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Phone</label>
                            <InputText value={editLeadData.phone || ''} onChange={(e) => setEditLeadData({...editLeadData, phone: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Email</label>
                            <InputText value={editLeadData.email || ''} onChange={(e) => setEditLeadData({...editLeadData, email: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>PAN</label>
                            <InputText value={editLeadData.pan || ''} onChange={(e) => setEditLeadData({...editLeadData, pan: e.target.value.toUpperCase()})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} maxLength={10} />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="col-12 mb-1 mt-1">
                        <h4 className="text-primary m-0 p-0" style={{ fontSize: '0.8rem' }}>Address</h4>
                    </div>
                    <div className="col-12 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Address</label>
                            <InputTextarea value={editLeadData.address || ''} onChange={(e) => setEditLeadData({...editLeadData, address: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} rows={2} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>City</label>
                            <InputText value={editLeadData.city || ''} onChange={(e) => setEditLeadData({...editLeadData, city: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>State</label>
                            <InputText value={editLeadData.state || ''} onChange={(e) => setEditLeadData({...editLeadData, state: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Pincode</label>
                            <InputText value={editLeadData.pincode || ''} onChange={(e) => setEditLeadData({...editLeadData, pincode: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} maxLength={6} />
                        </div>
                    </div>

                    {/* Loan Details */}
                    <div className="col-12 mb-1 mt-1">
                        <h4 className="text-primary m-0 p-0" style={{ fontSize: '0.8rem' }}>Loan Details</h4>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Loan Amount Required</label>
                            <InputNumber value={editLeadData.loan_amount_required || 0} onValueChange={(e) => setEditLeadData({...editLeadData, loan_amount_required: e.value})} mode="currency" currency="INR" locale="en-IN" className="w-full p-inputtext-sm" inputStyle={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>CIBIL Score</label>
                            <InputNumber value={editLeadData.cibil_score || null} onValueChange={(e) => setEditLeadData({...editLeadData, cibil_score: e.value})} className="w-full p-inputtext-sm" inputStyle={{ fontSize: '0.75rem' }} min={300} max={900} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Employment Type</label>
                            <InputText value={editLeadData.employment_type || ''} onChange={(e) => setEditLeadData({...editLeadData, employment_type: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>

                    {/* Financial Details */}
                    <div className="col-12 mb-1 mt-1">
                        <h4 className="text-primary m-0 p-0" style={{ fontSize: '0.8rem' }}>Financial Details</h4>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Company Name</label>
                            <InputText value={editLeadData.company_name || ''} onChange={(e) => setEditLeadData({...editLeadData, company_name: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Net Salary</label>
                            <InputNumber value={editLeadData.net_salary || null} onValueChange={(e) => setEditLeadData({...editLeadData, net_salary: e.value})} mode="currency" currency="INR" locale="en-IN" className="w-full p-inputtext-sm" inputStyle={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-4 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Annual Turnover</label>
                            <InputNumber value={editLeadData.annual_turnover || null} onValueChange={(e) => setEditLeadData({...editLeadData, annual_turnover: e.value})} mode="currency" currency="INR" locale="en-IN" className="w-full p-inputtext-sm" inputStyle={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="col-12 mb-1 mt-1">
                        <h4 className="text-primary m-0 p-0" style={{ fontSize: '0.8rem' }}>Additional Information</h4>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Preferred Banks</label>
                            <MultiSelect value={editLeadData.preferred_bank || []} options={bankOptions} onChange={(e) => setEditLeadData({...editLeadData, preferred_bank: e.value})} placeholder="Select preferred banks" filter showClear display="chip" className="w-full p-inputtext-sm" maxSelectedLabels={3} panelStyle={{ fontSize: '0.7rem' }} pt={{ input: { style: { fontSize: '0.75rem' } }, item: { style: { fontSize: '0.7rem', padding: '0.4rem 0.75rem' } }, filterInput: { style: { fontSize: '0.7rem' } }, token: { style: { fontSize: '0.65rem', padding: '0.15rem 0.4rem' } } }} />
                        </div>
                    </div>
                    <div className="col-12 md:col-6 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Referred By</label>
                            <InputText value={editLeadData.referred_by || ''} onChange={(e) => setEditLeadData({...editLeadData, referred_by: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} />
                        </div>
                    </div>
                    <div className="col-12 p-1">
                        <div className="field mb-2">
                            <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Notes</label>
                            <InputTextarea value={editLeadData.note || ''} onChange={(e) => setEditLeadData({...editLeadData, note: e.target.value})} className="w-full p-inputtext-sm" style={{ fontSize: '0.75rem' }} rows={2} />
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* MODULE 5C: Add Note Dialog */}
            <Dialog
                visible={showNoteDialog}
                onHide={() => setShowNoteDialog(false)}
                header={<div className="flex align-items-center gap-2"><i className="pi pi-comment text-primary" /><span>Add Note</span></div>}
                style={{ width: '500px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowNoteDialog(false)} />
                        <Button label="Add Note" icon="pi pi-plus" onClick={handleAddNote} loading={processing} disabled={!noteData.content.trim()} />
                    </div>
                }
            >
                <div className="field mb-3">
                    <label className="block text-900 font-medium mb-2">Note Content *</label>
                    <InputTextarea value={noteData.content} onChange={(e) => setNoteData({...noteData, content: e.target.value})} className="w-full" rows={5} placeholder="Enter your note..." autoResize />
                </div>
                <div className="flex align-items-center gap-2">
                    <input type="checkbox" id="pinNote" checked={noteData.is_pinned} onChange={(e) => setNoteData({...noteData, is_pinned: e.target.checked})} />
                    <label htmlFor="pinNote" className="text-900">Pin this note (visible at top)</label>
                </div>
            </Dialog>

            {/* Mobile Sticky Action Bar - visible only on mobile */}
            <div className="mobile-action-bar">
                {isOwner() ? (
                    /* Owner view */
                    <div className="mobile-action-content mobile-owner">
                        <div className="mobile-status-info">
                            <i className="pi pi-crown text-yellow-500" />
                            <div className="flex flex-column">
                                <span className="text-900 text-sm font-semibold">Owner</span>
                                <span className="text-500 text-xs">#{collaboration?.id?.slice(-8)}</span>
                            </div>
                        </div>
                        {isActive() ? (
                            <div className="flex gap-2">
                                <Button
                                    icon="pi pi-check-circle"
                                    label="Complete"
                                    className="p-button-sm p-button-success mobile-action-btn"
                                    onClick={() => setShowCompleteDialog(true)}
                                />
                            </div>
                        ) : (
                            <Tag
                                value={statusConfig.label}
                                icon={`pi ${statusConfig.icon}`}
                                style={{ ...outlineTagStyle(statusConfig.severity) }}
                            />
                        )}
                    </div>
                ) : (
                    /* Collaborator view */
                    <div className="mobile-action-content mobile-collaborator">
                        <div className="mobile-status-info">
                            <i className="pi pi-users text-blue-500" />
                            <div className="flex flex-column">
                                <span className="text-900 text-sm font-semibold">Collaborator</span>
                                <span className="text-primary text-xs font-medium">
                                    {collaboration?.my_commission_percentage || 0}% share
                                </span>
                            </div>
                        </div>
                        {isActive() ? (
                            <Button
                                icon="pi pi-sign-out"
                                label="Leave"
                                className="p-button-sm p-button-warning p-button-outlined mobile-action-btn"
                                onClick={() => setShowLeaveDialog(true)}
                            />
                        ) : (
                            <Tag
                                value={statusConfig.label}
                                icon={`pi ${statusConfig.icon}`}
                                style={{ ...outlineTagStyle(statusConfig.severity) }}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Embedded Styles for Mobile Responsiveness */}
            <style>{`
                /* Mobile Action Bar - Hidden by default (desktop) */
                .mobile-action-bar {
                    display: none;
                }

                /* Mobile Responsive Styles */
                @media (max-width: 991px) {
                    /* Show mobile action bar */
                    .mobile-action-bar {
                        display: block;
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background: white;
                        border-top: 1px solid #E2E8F0;
                        padding: 0.75rem 1rem;
                        z-index: 1000;
                        box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
                    }
                    .mobile-action-content {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        max-width: 100%;
                        gap: 1rem;
                    }
                    .mobile-status-info {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    .mobile-status-info i {
                        font-size: 1.25rem;
                    }
                    .mobile-action-btn {
                        white-space: nowrap;
                    }

                    /* Add bottom padding to main content to prevent overlap with fixed bar */
                    .collaboration-details {
                        padding-bottom: 80px;
                    }

                    /* Breadcrumb header compact on mobile */
                    .collaboration-details > .surface-card.border-round.shadow-1.p-3.mb-4 {
                        padding: 0.5rem 0.75rem !important;
                        margin-bottom: 0.75rem !important;
                    }

                    /* Quick stats compact */
                    .collaboration-details .grid.mb-4 > .col-6 > .text-center.p-3 {
                        padding: 0.5rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 i.text-2xl {
                        font-size: 1.25rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 .text-xl {
                        font-size: 1rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 .text-sm {
                        font-size: 0.7rem !important;
                    }

                    /* Lead card header compact */
                    .collaboration-details .text-2xl.md\\:text-3xl {
                        font-size: 1.25rem !important;
                    }

                    /* Borrower info section compact */
                    .collaboration-details .surface-50.border-round.p-4 {
                        padding: 0.75rem !important;
                    }
                    .collaboration-details .surface-50 .flex.align-items-center.gap-3 {
                        gap: 0.5rem !important;
                    }
                    .collaboration-details .surface-50 .bg-primary-100,
                    .collaboration-details .surface-50 .bg-green-100,
                    .collaboration-details .surface-50 .bg-blue-100,
                    .collaboration-details .surface-50 .bg-purple-100 {
                        width: 32px !important;
                        height: 32px !important;
                    }
                    .collaboration-details .surface-50 .text-xs {
                        font-size: 0.65rem !important;
                    }

                    /* Lead details grid compact */
                    .collaboration-details .col-6.md\\:col-4 .surface-card.border-round.p-3 {
                        padding: 0.5rem !important;
                    }
                    .collaboration-details .col-6.md\\:col-4 .font-semibold {
                        font-size: 0.85rem !important;
                    }
                    .collaboration-details .col-6.md\\:col-4 .text-xs {
                        font-size: 0.65rem !important;
                    }

                    /* Hide sidebar actions card on mobile (use mobile bar instead) */
                    .collaboration-details .col-12.lg\\:col-4 > .shadow-1.sticky {
                        display: none;
                    }

                    /* TabView compact */
                    .collaboration-details .p-tabview .p-tabview-nav {
                        overflow-x: auto;
                        flex-wrap: nowrap;
                    }
                    .collaboration-details .p-tabview .p-tabview-nav li {
                        flex-shrink: 0;
                    }
                    .collaboration-details .p-tabview .p-tabview-nav li a {
                        padding: 0.5rem 0.75rem !important;
                        font-size: 0.8rem !important;
                    }
                }

                /* Extra small screens (max-width: 576px) */
                @media (max-width: 576px) {
                    .mobile-action-bar {
                        padding: 0.625rem 0.75rem;
                    }
                    .mobile-status-info i {
                        font-size: 1rem;
                    }
                    .mobile-status-info .text-sm {
                        font-size: 0.75rem !important;
                    }
                    .mobile-status-info .text-xs {
                        font-size: 0.65rem !important;
                    }
                    .mobile-action-btn {
                        font-size: 0.8rem !important;
                        padding: 0.5rem 0.75rem !important;
                    }

                    /* Breadcrumb - hide trail on very small screens */
                    .collaboration-details > .surface-card .pi-angle-right {
                        display: none;
                    }
                    .collaboration-details > .surface-card > .flex > .flex > .text-500:not(:first-child) {
                        display: none;
                    }
                    .collaboration-details > .surface-card > .flex > .flex > .text-900:not(:first-child) {
                        display: none;
                    }

                    /* Hide Edit Lead button on mobile header */
                    .collaboration-details > .surface-card .p-button-outlined.p-button-sm {
                        display: none;
                    }

                    /* Quick stats 2x2 grid adjustments */
                    .collaboration-details .grid.mb-4 > .col-6 > .text-center {
                        padding: 0.375rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 i {
                        font-size: 1rem !important;
                        margin-bottom: 0.25rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 .text-xl {
                        font-size: 0.875rem !important;
                    }
                    .collaboration-details .grid.mb-4 > .col-6 .text-sm {
                        font-size: 0.625rem !important;
                    }

                    /* Borrower info even more compact */
                    .collaboration-details .surface-50.border-round {
                        padding: 0.5rem !important;
                    }

                    /* Lead details 2 columns stays same, just smaller text */
                    .collaboration-details .col-6.md\\:col-4 {
                        padding: 0.25rem !important;
                    }
                }

                /* Edit Lead Dialog compact styling */
                .edit-lead-dialog .p-dialog-content {
                    padding: 0.75rem 1rem !important;
                }
                .edit-lead-dialog .p-dialog-header {
                    padding: 0.6rem 1rem !important;
                }
                .edit-lead-dialog .p-dialog-footer {
                    padding: 0.5rem 1rem !important;
                }
                .edit-lead-dialog .p-inputtext {
                    padding: 0.4rem 0.5rem;
                    font-size: 0.75rem;
                }
                .edit-lead-dialog .p-inputnumber-input {
                    padding: 0.4rem 0.5rem;
                    font-size: 0.75rem;
                }
                .edit-lead-dialog .p-inputtextarea {
                    padding: 0.4rem 0.5rem;
                    font-size: 0.75rem;
                }
                .edit-lead-dialog .p-multiselect {
                    font-size: 0.75rem;
                }
                .edit-lead-dialog .p-multiselect .p-multiselect-label {
                    padding: 0.4rem 0.5rem;
                    font-size: 0.75rem;
                }
                .edit-lead-dialog .p-multiselect .p-multiselect-trigger {
                    width: 2rem;
                }
                .edit-lead-dialog .p-multiselect-token {
                    font-size: 0.65rem;
                    padding: 0.1rem 0.35rem;
                }
                .edit-lead-dialog .p-message {
                    padding: 0.4rem 0.6rem;
                }
                .edit-lead-dialog .p-message .p-message-text {
                    font-size: 0.7rem;
                }
            `}</style>
        </div>
    );
};

export default CollaborationDetails;
